// src/theme/GlobalStyles.tsx

const css = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    /* ---------------- LIGHT THEME — navy ink + teal V (styles.css) ---------------- */
    --navy:   #142855;
    --navy-2: #0A1838;
    --navy-3: #1F3A78;
    --teal:   #8FCFC1;
    --teal-2: #62B5A3;
    --teal-3: #C6E6DE;
    --teal-50:#F0F8F5;

    --gold-50:  #fff8e7;
    --gold-100: #ffe9a0;
    --gold-300: #f5d76e;
    --gold-500: #d4a017;
    --gold-600: #c9a227;
    --warm-50:  #e8dcc0;
    --warm-100: #d4c9a8;
    --warm-200: #c9c2b4;
    --warm-500: #9a958a;
    --warm-900: #1f1a12;
    --warm-950: #14110c;
    --slate-950:#0f141f;
    --brown-950:#1a1408;

    --bg:      #FFFFFF;
    --surface: #FFFFFF;
    --text:    #0F1B36;
    --muted:   #4A5468;
    --border:  #E8EAF0;

    --primary:     var(--navy);
    --primary-fg:  #ffffff;
    --secondary:   var(--teal);

    --success: #16a34a;
    --warning: #d97706;
    --error:   #dc2626;
    --info:    #1F3A78;

    --info-light:    color-mix(in srgb, #1F3A78 10%, transparent);
    --info-lighter:  color-mix(in srgb, #1F3A78 20%, transparent);
    --info-border:   color-mix(in srgb, #1F3A78 30%, transparent);
    --warning-light: color-mix(in srgb, #d97706 10%, transparent);
    --warning-lighter: color-mix(in srgb, #d97706 20%, transparent);
    --warning-border:  color-mix(in srgb, #d97706 30%, transparent);
    --success-light: color-mix(in srgb, #16a34a 10%, transparent);
    --success-lighter: color-mix(in srgb, #16a34a 20%, transparent);
    --success-border:  color-mix(in srgb, #16a34a 30%, transparent);
    --error-light:   color-mix(in srgb, #dc2626 10%, transparent);
    --error-lighter: color-mix(in srgb, #dc2626 20%, transparent);
    --error-border:  color-mix(in srgb, #dc2626 30%, transparent);

    /* Brand aliases (styles.css) */
    --ink:       var(--text);
    --ink-2:     #2A3553;
    --ink-3:     #4A5468;
    --ink-4:     #8590A7;
    --ink-5:     #C8CDDB;
    --fg:        var(--ink);
    --fg-2:      var(--ink-2);
    --fg-3:      var(--ink-3);
    --fg-4:      var(--ink-4);
    --accent:    var(--navy);
    --accent-2:  var(--navy-2);
    --highlight: var(--teal);
    --line:      var(--border);
    --line-2:    #DDE0EA;
    --line-strong: #142855;
    --bg-2:      #FAFAF7;
    --bg-3:      #F4F4EF;
    --bg-card:   #FFFFFF;

    /* ExecEdge Comms dashboard accent gradients */
    --ee-comms-purple-gradient: linear-gradient(135deg, #667eea, #764ba2);
    --ee-comms-whatsapp-gradient: linear-gradient(135deg, #25d366, #128c7e);
    --ee-comms-stat-indigo: linear-gradient(135deg, var(--teal-50), var(--teal-3));
    --ee-comms-stat-amber: linear-gradient(135deg, #fef3c7, #fde68a);
    --ee-comms-stat-green: linear-gradient(135deg, #d1fae5, #a7f3d0);
    --ee-comms-stat-purple: linear-gradient(135deg, var(--teal-50), var(--teal-3));

    --shadow-card: 0 6px 16px -4px rgba(15, 27, 54, 0.08), 0 2px 6px -2px rgba(15, 27, 54, 0.04);
    --shadow-popover: 0 24px 48px -16px rgba(15, 27, 54, 0.14), 0 8px 16px -8px rgba(15, 27, 54, 0.06);
    --shadow-card-hover: 0 28px 56px -20px rgba(20, 40, 85, 0.22), 0 8px 20px -8px rgba(20, 40, 85, 0.10);

    /* ── Backgrounds ── */
    --bg-page:     var(--bg-2);
    --bg-surface:  var(--surface);
    --bg-subtle:   var(--bg-2);
    --bg-muted:    var(--bg-3);
    --bg-elevated: var(--surface);

    /* ── Text ── */
    --text-primary:   var(--text);
    --text-secondary: var(--ink-2);
    --text-muted:     var(--muted);
    --text-inverse:   #ffffff;
    --text-danger:    var(--error);
    --text-success:   var(--success);
    --text-warning:   var(--warning);
    --text-info:      var(--info);

    /* ── Borders ── */
    --border-subtle:  var(--line);
    --border-default: var(--line-2);
    --border-strong:  var(--line-strong);

    /* ── Interactive ── */
    --interactive-primary:        var(--navy);
    --interactive-primary-hover:  var(--navy-3);
    --interactive-primary-text:   var(--primary-fg);
    --interactive-secondary:      var(--bg);
    --interactive-secondary-hover:var(--navy);
    --interactive-secondary-text: var(--navy);
    --interactive-danger:         #dc2626;
    --interactive-danger-hover:   #991b1b;

    --btn-primary-label:            var(--primary-fg);
    --on-accent:                    #ffffff;
    --apply-step-chip:              var(--text-primary);

    /* ── Controls / overlays ── */
    --control-bg:          var(--bg-surface);
    --control-bg-hover:    var(--bg-2);
    --control-bg-selected: var(--teal-50);
    --control-text:        var(--text-primary);
    --control-placeholder: var(--ink-4);
    --popover-bg:          var(--bg-elevated);
    --popover-text:        var(--text-primary);
    --focus-ring:          rgba(20, 40, 85, 0.08);

    /* ── Typography ── */
    --font-sans: "Geist", -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
    --font-mono: "JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace;

    /* ── Radius ── */
    --radius-sm:   8px;
    --radius-md:   12px;
    --radius-lg:   16px;
    --radius-xl:   24px;
    --radius-2xl:  24px;
    --radius-full: 999px;

    /* ── Shadows (styles.css) ── */
    --shadow-sm: 0 1px 2px rgba(15, 27, 54, 0.04), 0 1px 1px rgba(15, 27, 54, 0.02);
    --shadow-md: 0 6px 16px -4px rgba(15, 27, 54, 0.08), 0 2px 6px -2px rgba(15, 27, 54, 0.04);
    --shadow-lg: 0 24px 48px -16px rgba(15, 27, 54, 0.14), 0 8px 16px -8px rgba(15, 27, 54, 0.06);
    --shadow-xl: var(--shadow-lg);

    /* ── Transitions ── */
    --transition-fast:   100ms ease;
    --transition-normal: 150ms ease;
    --transition-slow:   300ms ease;

    /* ── Z-index ── */
    --z-dropdown: 100;
    --z-sticky:   200;
    --z-overlay:  300;
    --z-modal:    400;
    --z-toast:    500;

    /* Solid navy. Does not flip in light mode — video stages and badges. */
    --bg-dark: #0A1124;

    /* Page dimmer behind drawers, dialogs, and sidebars. Stays dark in both themes. */
    --overlay-scrim: color-mix(in srgb, var(--bg-dark) 55%, transparent);

    /* HV Role modal (RoleModal.jsx) — tab rail & dialog chrome (compose from tokens) */
    --role-tab-basic: var(--interactive-primary);
    --role-tab-jd: var(--success);
    --role-tab-assessment: var(--warning);
    --role-tab-requirements: var(--info);
    --role-tab-log: color-mix(in srgb, var(--interactive-primary) 50%, var(--info) 50%);
    --role-tab-poc: var(--secondary);
    --role-tab-email: var(--error);
    --role-dialog-divider-shine: color-mix(in srgb, var(--interactive-primary) 28%, transparent);

    /* ExecEdge Project Timeline — phase accents */
    --ee-tl-onboard: #62B5A3;
    --ee-tl-sprint1: #d97706;
    --ee-tl-sprint2: #dc2626;
    --ee-tl-sprint3: #7c3aed;
    --ee-tl-glow-onboard: color-mix(in srgb, var(--ee-tl-onboard) 30%, transparent);
    --ee-tl-glow-sprint1: color-mix(in srgb, var(--ee-tl-sprint1) 30%, transparent);
    --ee-tl-glow-sprint2: color-mix(in srgb, var(--ee-tl-sprint2) 30%, transparent);
    --ee-tl-glow-sprint3: color-mix(in srgb, var(--ee-tl-sprint3) 30%, transparent);
    --ee-tl-card: color-mix(in srgb, var(--bg-surface) 88%, transparent);
    --ee-tl-onboard-solid-label: var(--primary-fg);
    --ee-tl-sprint1-solid-label: var(--primary-fg);
    --ee-tl-sprint2-solid-label: #fff;
    --ee-tl-sprint3-solid-label: #fff;
    --ee-tl-progress-gradient: linear-gradient(
      90deg,
      var(--ee-tl-onboard),
      var(--ee-tl-sprint1),
      var(--ee-tl-sprint2),
      var(--ee-tl-sprint3)
    );

    /* ExecEdge cream landing (.eeb in funnel.tsx) */
    --eeb-bg: #f5f5f1;
    --eeb-text: #15171c;
    --eeb-muted: #5c6169;
    --eeb-gold: #c9a227;
    --eeb-on-gold: #15171c;
    --eeb-gold-text: #7a5c10;
    --eeb-gold-tint: #f6efdc;
    --eeb-surface: #ffffff;
    --eeb-surface-2: #efefea;
    --eeb-rule: #dcddd7;
    --eeb-hv: #2a2e78;
    --eeb-hv-tint: #e9eaf3;
    --eeb-warning: #9b2c2c;
    --eeb-backdrop: color-mix(in srgb, #15171c 45%, transparent);
    --eeb-sans: var(--font-sans);
    --eeb-serif: Georgia, "Times New Roman", serif;
    --eeb-mono: var(--font-mono);
  }
  .dark,
  [data-theme="dark"] {
    /* ---------------- DARK THEME — navy ink + teal V (styles.css) ---------------- */
    --bg:      #0A1124;
    --overlay-scrim: color-mix(in srgb, var(--bg-dark) 55%, transparent);
    --surface: #14213F;
    --text:    #F2F4FA;
    --muted:   #A6B0C8;
    --border:  #1F2C4F;

    --primary:     var(--teal);
    --primary-fg:  #000000;
    --secondary:   var(--teal-2);
    --btn-primary-label: #000000;
    --on-accent:         var(--navy);
    --apply-step-chip:   var(--navy);

    --success: #4ade80;
    --warning: #fbbf24;
    --error:   #f87171;
    --info:    var(--teal);

    --info-light:    color-mix(in srgb, var(--teal) 10%, transparent);
    --info-lighter:  color-mix(in srgb, var(--teal) 20%, transparent);
    --info-border:   color-mix(in srgb, var(--teal) 30%, transparent);
    --warning-light: color-mix(in srgb, #fbbf24 10%, transparent);
    --warning-lighter: color-mix(in srgb, #fbbf24 20%, transparent);
    --warning-border:  color-mix(in srgb, #fbbf24 30%, transparent);
    --success-light: color-mix(in srgb, #4ade80 10%, transparent);
    --success-lighter: color-mix(in srgb, #4ade80 20%, transparent);
    --success-border:  color-mix(in srgb, #4ade80 30%, transparent);
    --error-light:   color-mix(in srgb, #f87171 10%, transparent);
    --error-lighter: color-mix(in srgb, #f87171 20%, transparent);
    --error-border:  color-mix(in srgb, #f87171 30%, transparent);

    --ink:       var(--text);
    --ink-2:     #DCE0EE;
    --ink-3:     #A6B0C8;
    --ink-4:     #7782A0;
    --ink-5:     #444E6B;
    --fg:        var(--ink);
    --fg-2:      var(--ink-2);
    --fg-3:      var(--ink-3);
    --fg-4:      var(--ink-4);
    --accent:    var(--teal);
    --accent-2:  var(--teal-2);
    --highlight: var(--teal);
    --line:      var(--border);
    --line-2:    #2A3960;
    --line-strong: var(--teal);
    --bg-2:      #0E1730;
    --bg-3:      #14213F;
    --bg-card:   #14213F;

    --ee-comms-purple-gradient: linear-gradient(135deg, #4f5fc7, #5e3a82);
    --ee-comms-whatsapp-gradient: linear-gradient(135deg, #1f9d4d, #0e6e63);
    --ee-comms-stat-indigo: linear-gradient(135deg, color-mix(in srgb, var(--teal) 18%, transparent), color-mix(in srgb, var(--teal) 8%, transparent));
    --ee-comms-stat-amber: linear-gradient(135deg, color-mix(in srgb, #fbbf24 18%, transparent), color-mix(in srgb, #fbbf24 8%, transparent));
    --ee-comms-stat-green: linear-gradient(135deg, color-mix(in srgb, #4ade80 18%, transparent), color-mix(in srgb, #4ade80 8%, transparent));
    --ee-comms-stat-purple: linear-gradient(135deg, color-mix(in srgb, var(--teal) 18%, transparent), color-mix(in srgb, var(--teal) 8%, transparent));

    --shadow-card: 0 6px 16px -4px rgba(0, 0, 0, 0.55);
    --shadow-popover: 0 24px 48px -16px rgba(0, 0, 0, 0.6);
    --shadow-card-hover: 0 28px 56px -20px rgba(0, 0, 0, 0.6);

    --bg-page:     var(--bg);
    --bg-surface:  var(--surface);
    --bg-subtle:   var(--bg-2);
    --bg-muted:    var(--bg-3);
    --bg-elevated: var(--bg-3);

    --text-primary:   var(--text);
    --text-secondary: var(--ink-2);
    --text-muted:     var(--muted);

    --border-subtle:  var(--line);
    --border-default: var(--line-2);
    --border-strong:  var(--line-strong);

    --text-danger:  var(--error);
    --text-success: var(--success);
    --text-warning: var(--warning);
    --text-info:    var(--info);

    --interactive-primary:        var(--teal);
    --interactive-primary-hover:  var(--teal-2);
    --interactive-primary-text:   #000000;
    --interactive-secondary:      var(--bg-3);
    --interactive-secondary-hover:  var(--teal);
    --interactive-secondary-text: var(--ink);

    --control-bg:          var(--bg-3);
    --control-bg-hover:    var(--bg-2);
    --control-bg-selected: color-mix(in srgb, var(--teal) 18%, transparent);
    --control-text:        var(--text-primary);
    --control-placeholder: var(--ink-4);
    --popover-bg:          var(--bg-elevated);
    --popover-text:        var(--text-primary);
    --focus-ring:          rgba(143, 207, 193, 0.18);

    --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.4);
    --shadow-md: 0 6px 16px -4px rgba(0, 0, 0, 0.55), 0 2px 6px -2px rgba(0, 0, 0, 0.4);
    --shadow-lg: 0 24px 48px -16px rgba(0, 0, 0, 0.6);
    --shadow-xl: var(--shadow-lg);

    --ee-tl-onboard: var(--teal);
    --ee-tl-sprint1: #fbbf24;
    --ee-tl-sprint2: #f87171;
    --ee-tl-sprint3: #a78bfa;
    --ee-tl-glow-onboard: color-mix(in srgb, var(--ee-tl-onboard) 35%, transparent);
    --ee-tl-glow-sprint1: color-mix(in srgb, var(--ee-tl-sprint1) 35%, transparent);
    --ee-tl-glow-sprint2: color-mix(in srgb, var(--ee-tl-sprint2) 35%, transparent);
    --ee-tl-glow-sprint3: color-mix(in srgb, var(--ee-tl-sprint3) 35%, transparent);
    --ee-tl-card: color-mix(in srgb, var(--bg-surface) 75%, transparent);
    --ee-tl-onboard-solid-bg: var(--teal);
    --ee-tl-onboard-solid-label: #000000;
    --ee-tl-sprint1-solid-bg: #d97706;
    --ee-tl-sprint1-solid-label: #fff;
    --ee-tl-sprint2-solid-bg: #dc2626;
    --ee-tl-sprint2-solid-label: #fff;
    --ee-tl-sprint3-solid-bg: #7c3aed;
    --ee-tl-sprint3-solid-label: #fff;
    --ee-tl-progress-gradient: linear-gradient(
      90deg,
      var(--ee-tl-onboard),
      var(--ee-tl-sprint1),
      var(--ee-tl-sprint2),
      var(--ee-tl-sprint3)
    );

    --eeb-bg: #101218;
    --eeb-text: #e6e4de;
    --eeb-muted: #9aa0aa;
    --eeb-gold: #c9a227;
    --eeb-on-gold: #15171c;
    --eeb-gold-text: #c9a227;
    --eeb-gold-tint: #26200e;
    --eeb-surface: #171a22;
    --eeb-surface-2: #1c1f28;
    --eeb-rule: #2c303a;
    --eeb-hv: #a3a9ee;
    --eeb-hv-tint: #1b1f35;
    --eeb-warning: #e4756f;
    --eeb-backdrop: color-mix(in srgb, #000 55%, transparent);
    --eeb-sans: var(--font-sans);
    --eeb-serif: Georgia, "Times New Roman", serif;
    --eeb-mono: var(--font-mono);
  }
  html, body {
    font-family: var(--font-sans);
    font-size: 14px;
    line-height: 1.5;
    color: var(--text-primary);
    background: var(--bg-page);
    color-scheme: light;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  html[data-theme="dark"],
  html.dark {
    color-scheme: dark;
  }

  ::selection {
    background: var(--teal);
    color: var(--navy);
  }

  ::-webkit-scrollbar       { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--border-strong); border-radius: var(--radius-full); }

  :focus-visible {
    outline: 2px solid var(--interactive-primary);
    outline-offset: 2px;
  }

  button { font-family: var(--font-sans); cursor: pointer; }

  /* Teal primary buttons — black label in dark (MUI CssBaseline used to force span color) */
  html[data-theme="dark"] .hv-btn-primary,
  html[data-theme="dark"] a.hv-btn-primary,
  html[data-theme="dark"] button.hv-btn-primary,
  html[data-theme="dark"] .bg-primary.border-primary,
  html[data-theme="dark"] a.bg-primary.border-primary,
  html[data-theme="dark"] button.bg-primary.border-primary {
    color: #000000 !important;
  }
  html[data-theme="dark"] .hv-btn-primary span,
  html[data-theme="dark"] a.hv-btn-primary span,
  html[data-theme="dark"] button.hv-btn-primary span,
  html[data-theme="dark"] .bg-primary.border-primary span {
    color: #000000 !important;
  }
  html[data-theme="dark"] .hv-btn-primary:hover,
  html[data-theme="dark"] a.hv-btn-primary:hover,
  html[data-theme="dark"] button.hv-btn-primary:hover,
  html[data-theme="dark"] .bg-primary.border-primary:hover span {
    color: #000000 !important;
  }

  /* Timeline form CTA — MUI CssBaseline forces light span text in dark mode */
  .ee-tl-form-cta,
  .ee-tl-form-cta svg {
    color: inherit;
  }
  html[data-theme="dark"] .ee-tl-form-cta--onboard,
  html[data-theme="dark"] .ee-tl-form-cta--onboard span {
    background: var(--ee-tl-onboard-solid-bg) !important;
    color: #000000 !important;
  }
  html[data-theme="dark"] .ee-tl-form-cta--sprint1,
  html[data-theme="dark"] .ee-tl-form-cta--sprint1 span {
    background: var(--ee-tl-sprint1-solid-bg) !important;
    color: #ffffff !important;
  }
  html[data-theme="dark"] .ee-tl-form-cta--sprint2,
  html[data-theme="dark"] .ee-tl-form-cta--sprint2 span {
    background: var(--ee-tl-sprint2-solid-bg) !important;
    color: #ffffff !important;
  }
  html[data-theme="dark"] .ee-tl-form-cta--sprint3,
  html[data-theme="dark"] .ee-tl-form-cta--sprint3 span {
    background: var(--ee-tl-sprint3-solid-bg) !important;
    color: #ffffff !important;
  }

  input, textarea, select {
    font-family: var(--font-sans);
    background-color: var(--control-bg);
    color: var(--control-text);
    border-color: var(--border-default);
  }
  label,
  .MuiFormLabel-root,
  .MuiInputLabel-root,
  .MuiInputLabel-outlined,
  .MuiNativeSelect-icon,
  .MuiSelect-icon {
    color: var(--text-secondary);
  }
  .MuiFormLabel-root.Mui-focused,
  .MuiInputLabel-root.Mui-focused,
  .MuiInputLabel-outlined.Mui-focused {
    color: var(--interactive-primary);
  }
  .MuiFormLabel-root.Mui-error,
  .MuiInputLabel-root.Mui-error {
    color: var(--text-danger);
  }
  .MuiInputLabel-shrink {
    background-color: var(--bg-surface);
    padding-left: 4px;
    padding-right: 4px;
  }
  .MuiInputBase-root,
  .MuiSelect-select,
  .MuiNativeSelect-select {
    color: var(--control-text);
  }
  input::placeholder,
  textarea::placeholder {
    color: var(--control-placeholder);
    opacity: 1;
  }
  select,
  option,
  optgroup {
    background-color: var(--popover-bg);
    color: var(--popover-text);
  }
  option:checked {
    background-color: var(--control-bg-selected);
    color: var(--control-text);
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  .spinner {
    width: 36px; height: 36px;
    border: 3px solid var(--border-default);
    border-top-color: var(--interactive-primary);
    border-radius: var(--radius-full);
    animation: spin 0.75s linear infinite;
    flex-shrink: 0;
  }
  .spinner--sm { width: 20px; height: 20px; border-width: 2px; }
  .spinner--lg { width: 52px; height: 52px; border-width: 4px; }

  /* ── Card ── */
  /* FIX: card background must be --bg-surface (white), border bumped to [200] */
  .card {
    background:    var(--bg-surface);
    border:        1px solid var(--border-default);
    border-radius: var(--radius-xl);
    box-shadow:    var(--shadow-md);
    overflow:      hidden;
  }
  .card--flat   { box-shadow: none; }
  .card--hover  { transition: box-shadow var(--transition-normal), transform var(--transition-normal); cursor: pointer; }
  .card--hover:hover { box-shadow: var(--shadow-lg); transform: translateY(-1px); }
  .card--padded { padding: 24px; }

  /* ── Avatar ── */
  .avatar {
    border-radius: var(--radius-full);
    object-fit:    cover;
    flex-shrink:   0;
    display:       flex;
    align-items:   center;
    justify-content: center;
    font-weight:   600;
    background:    var(--interactive-primary);
    color:         var(--interactive-primary-text);
  }
  .avatar--sm  { width: 28px;  height: 28px;  font-size: 10px; }
  .avatar--md  { width: 40px;  height: 40px;  font-size: 14px; }
  .avatar--lg  { width: 64px;  height: 64px;  font-size: 22px; }
  .avatar--xl  { width: 96px;  height: 96px;  font-size: 32px; }

  /* ── Metric card ── */
  /* FIX: was using hardcoded padding px that matched old bg — now bg-surface */
  .metric-card {
    background:    var(--bg-surface);
    border:        1px solid var(--border-default);
    border-radius: var(--radius-xl);
    box-shadow:    var(--shadow-sm);
    padding:       16px;
    display:       flex;
    align-items:   center;
    gap:           12px;
    position:      relative;
    overflow:      hidden;
  }
  .metric-card::before {
    content:    '';
    position:   absolute;
    left: 0; top: 0; bottom: 0;
    width:      3px;
    background: var(--interactive-primary);
  }
  .metric-card__icon {
    background:    var(--interactive-primary);
    color:         var(--interactive-primary-text);
    border-radius: var(--radius-full);
    padding:       10px;
    display:       flex;
    flex-shrink:   0;
  }
  .metric-card__label {
    font-size:   11px;
    color:       var(--text-muted);
    margin:      0;
    font-family: var(--font-sans);
  }
  .metric-card__value {
    font-size:   18px;
    font-weight: 700;
    color:       var(--text-primary);
    margin:      0;
    font-family: var(--font-sans);
  }

  /* ── Progress ring ── */
  .progress-ring {
    border-radius: var(--radius-full);
    display:       flex;
    align-items:   center;
    justify-content: center;
  }
  .progress-ring__inner {
    width:         88%;
    height:        88%;
    border-radius: var(--radius-full);
    background:    var(--bg-surface);
    display:       flex;
    align-items:   center;
    justify-content: center;
  }
  .progress-ring__label {
    font-size:     11px;
    font-weight:   700;
    color:         var(--text-success);
    background:    var(--bg-surface);
    padding:       1px 6px;
    border-radius: var(--radius-full);
    border:        1px solid var(--border-default);
  }

  .profile-progress-ring {
    background: conic-gradient(
      from 180deg,
      var(--success) 0deg 28.8deg,
      var(--border) 28.8deg 360deg
    );
  }

  /* ── Divider ── */
  .divider {
    border:     none;
    border-top: 1px solid var(--border-subtle);
    margin:     16px 0;
  }

  /* ── Loading screen ── */
  .loading-screen {
    display:         flex;
    flex-direction:  column;
    align-items:     center;
    justify-content: center;
    height:          80vh;
    gap:             12px;
  }
  .loading-screen__text {
    font-size:   14px;
    color:       var(--text-muted);
    font-family: var(--font-sans);
    margin:      0;
  }

  /* ── Section label ── */
  .section-label {
    font-size:      11px;
    font-weight:    600;
    color:          var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin:         0 0 8px;
    font-family:    var(--font-sans);
  }

  /* ── Form controls ── */
  /* FIX: inputs now use bg-surface (white) not bg-subtle (grey) — readable */
  .field-input {
    width:         100%;
    padding:       8px 12px;
    font-size:     13px;
    border:        1px solid var(--border-default);
    border-radius: var(--radius-md);
    background:    var(--bg-surface);
    color:         var(--text-primary);
    font-family:   var(--font-sans);
    outline:       none;
    box-sizing:    border-box;
    transition:    border-color var(--transition-fast), box-shadow var(--transition-fast);
  }
  .field-input::placeholder {
    color: var(--control-placeholder);
    opacity: 1;
  }
  .field-input:hover  { border-color: var(--border-strong); }
  .field-input:focus  {
    border-color: var(--interactive-primary);
    box-shadow:   0 0 0 3px #C6E6DE;
  }
  .field-input--search { padding-left: 36px; }
  .field-textarea      { resize: vertical; min-height: 96px; }

  /* ── Tooltip ── */
  .tooltip-content {
    background:    var(--bg-surface);
    border:        1px solid var(--border-default);
    border-radius: var(--radius-md);
    padding:       4px 8px;
    font-size:     11px;
    color:         var(--text-secondary);
    box-shadow:    var(--shadow-md);
    z-index:       999;
    font-family:   var(--font-sans);
  }

  /* ── Automate Emails admin dashboard ── */
  .automate-emails {
    font-family: var(--font-sans);
    color: var(--text-primary);
  }
  .automate-emails__header {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  @media (min-width: 640px) {
    .automate-emails__header {
      flex-direction: row;
      align-items: flex-start;
      justify-content: space-between;
    }
  }
  .automate-emails__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-primary);
    letter-spacing: -0.02em;
  }
  @media (min-width: 640px) {
    .automate-emails__title { font-size: 1.5rem; }
  }
  .automate-emails__subtitle {
    margin: 6px 0 0;
    font-size: 0.8125rem;
    line-height: 1.45;
    color: var(--text-muted);
    max-width: 52rem;
  }
  .automate-emails__segment {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 4px;
    border-radius: var(--radius-lg);
    border: 1px solid var(--border-default);
    background: var(--bg-subtle);
  }
  .automate-emails__segment-btn {
    appearance: none;
    border: 0;
    cursor: pointer;
    font-family: var(--font-sans);
    font-size: 0.8125rem;
    font-weight: 550;
    line-height: 1.2;
    padding: 8px 14px;
    border-radius: var(--radius-md);
    color: var(--text-secondary);
    background: transparent;
    transition: background var(--transition-fast), color var(--transition-fast), box-shadow var(--transition-fast);
  }
  .automate-emails__segment-btn:hover {
    color: var(--text-primary);
    background: color-mix(in srgb, var(--bg-surface) 70%, transparent);
  }
  .automate-emails__segment-btn.is-active {
    color: var(--interactive-primary-text);
    background: var(--interactive-primary);
    box-shadow: var(--shadow-sm);
  }
  .automate-emails__type-rail {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .automate-emails__type-btn {
    appearance: none;
    cursor: pointer;
    text-align: left;
    font-family: var(--font-sans);
    min-width: min(100%, 180px);
    flex: 1 1 160px;
    padding: 12px 14px;
    border-radius: var(--radius-lg);
    border: 1px solid var(--border-default);
    background: var(--bg-surface);
    color: var(--text-primary);
    box-shadow: var(--shadow-sm);
    transition: border-color var(--transition-fast), box-shadow var(--transition-fast), background var(--transition-fast);
  }
  .automate-emails__type-btn:hover {
    border-color: var(--border-strong);
  }
  .automate-emails__type-btn.is-active {
    border-color: color-mix(in srgb, var(--interactive-primary) 45%, var(--border-default));
    background: color-mix(in srgb, var(--interactive-primary) 8%, var(--bg-surface));
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--interactive-primary) 20%, transparent);
  }
  .automate-emails__type-btn.is-paused {
    border-color: var(--warning-border);
    background: color-mix(in srgb, var(--warning) 8%, var(--bg-surface));
  }
  .automate-emails__paused-badge {
    display: inline-flex;
    align-items: center;
    margin-left: 8px;
    padding: 2px 8px;
    border-radius: var(--radius-full);
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    vertical-align: middle;
    color: var(--text-warning);
    background: var(--warning-light);
    border: 1px solid var(--warning-border);
  }
  .automate-emails__type-btn-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 600;
  }
  .automate-emails__type-btn-hint {
    display: block;
    margin-top: 4px;
    font-size: 0.6875rem;
    font-weight: 500;
    color: var(--text-muted);
    line-height: 1.35;
  }
  .automate-emails__schedule {
    font-size: 0.8125rem;
    color: var(--text-muted);
  }
  .automate-emails__schedule strong {
    color: var(--text-primary);
    font-weight: 600;
  }
  .automate-emails__toggle-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    border-radius: var(--radius-lg);
    border: 1px solid var(--border-default);
    background: var(--bg-surface);
  }
  .automate-emails__toggle-row.is-off {
    border-color: var(--warning-border);
    background: var(--warning-light);
  }
  .automate-emails__toggle-copy {
    min-width: 0;
    flex: 1 1 200px;
  }
  .automate-emails__toggle-title {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 650;
    color: var(--text-primary);
  }
  .automate-emails__toggle-hint {
    margin: 4px 0 0;
    font-size: 0.75rem;
    color: var(--text-muted);
    line-height: 1.4;
  }
  .automate-emails__toggle-control {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
  .automate-emails__toggle-state {
    font-size: 0.75rem;
    font-weight: 650;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }
  .automate-emails__toggle-state.is-on { color: var(--text-success); }
  .automate-emails__toggle-state.is-off { color: var(--text-warning); }
  .automate-emails__switch {
    background: var(--bg-muted) !important;
    border-color: transparent !important;
  }
  .automate-emails__switch[aria-checked="true"] {
    background: var(--interactive-primary) !important;
  }
  .automate-emails__filters {
    display: grid;
    gap: 12px;
  }
  @media (min-width: 768px) {
    .automate-emails__filters {
      grid-template-columns: minmax(140px, 200px) 1fr auto;
      align-items: end;
    }
  }
  .automate-emails__field-label {
    display: block;
    margin-bottom: 6px;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--text-muted);
  }
  .automate-emails__check {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.8125rem;
    color: var(--text-primary);
    cursor: pointer;
    user-select: none;
  }
  .automate-emails__check input {
    accent-color: var(--interactive-primary);
  }
  .automate-emails__list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .automate-emails__row {
    border: 1px solid var(--border-default);
    border-radius: var(--radius-xl);
    background: var(--bg-surface);
    overflow: hidden;
    transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
  }
  .automate-emails__row:hover {
    border-color: var(--border-strong);
  }
  .automate-emails__row.is-open {
    border-color: color-mix(in srgb, var(--interactive-primary) 40%, var(--border-default));
    box-shadow: var(--shadow-sm);
  }
  .automate-emails__row.is-muted {
    border-color: var(--warning-border);
    background: color-mix(in srgb, var(--warning) 6%, var(--bg-surface));
  }
  .automate-emails__row-layout {
    display: flex;
    align-items: stretch;
    gap: 0;
  }
  .automate-emails__row-btn {
    appearance: none;
    flex: 1 1 auto;
    min-width: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    text-align: left;
    padding: 14px 16px;
    font-family: var(--font-sans);
    color: inherit;
  }
  .automate-emails__row-toggle {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex-shrink: 0;
    padding: 12px 14px;
    border-left: 1px solid var(--border-default);
    background: color-mix(in srgb, var(--bg-muted) 40%, var(--bg-surface));
  }
  .automate-emails__schedule-note {
    color: var(--text-muted);
    font-weight: 400;
  }
  .automate-emails__row-btn:hover {
    background: color-mix(in srgb, var(--bg-muted) 35%, transparent);
  }
  .automate-emails__row-top {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
  }
  .automate-emails__email {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--text-primary);
    word-break: break-all;
  }
  .automate-emails__subject {
    margin: 4px 0 0;
    font-size: 0.8125rem;
    color: var(--text-muted);
    line-height: 1.4;
  }
  .automate-emails__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 14px;
    margin-top: 10px;
    font-size: 0.75rem;
    color: var(--text-muted);
  }
  .automate-emails__meta-fail {
    color: var(--text-danger);
    font-weight: 600;
  }
  .automate-emails__issue {
    margin: 8px 0 0;
    font-size: 0.75rem;
    color: var(--text-danger);
  }
  .automate-emails__expand {
    border-top: 1px solid var(--border-default);
    background: var(--bg-subtle);
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .automate-emails__attempt {
    border: 1px solid var(--border-default);
    border-radius: var(--radius-lg);
    background: var(--bg-surface);
    padding: 12px 14px;
  }
  .automate-emails__attempt-top {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
  }
  .automate-emails__fail-box {
    margin-top: 10px;
    padding: 10px 12px;
    border-radius: var(--radius-md);
    border: 1px solid var(--error-border);
    background: var(--error-light);
    color: var(--text-danger);
  }
  .automate-emails__fail-box-title {
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 650;
  }
  .automate-emails__fail-box-body {
    margin: 4px 0 0;
    font-size: 0.8125rem;
    opacity: 0.95;
  }
  .automate-emails__body-preview {
    margin-top: 10px;
    max-height: 280px;
    overflow: auto;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
    background: var(--bg-surface);
    padding: 12px;
    font-size: 0.8125rem;
    color: var(--text-primary);
  }
  .automate-emails__body-preview a {
    color: var(--text-info);
    text-decoration: underline;
  }
  .automate-emails__status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: var(--radius-full);
    font-size: 0.6875rem;
    font-weight: 650;
    letter-spacing: 0.01em;
    border: 1px solid transparent;
    white-space: nowrap;
  }
  .automate-emails__status--ok {
    color: var(--text-success);
    background: var(--success-light);
    border-color: var(--success-border);
  }
  .automate-emails__status--fail {
    color: var(--text-danger);
    background: var(--error-light);
    border-color: var(--error-border);
  }
  .automate-emails__status--warn {
    color: var(--text-warning);
    background: var(--warning-light);
    border-color: var(--warning-border);
  }
  .automate-emails__status--muted {
    color: var(--text-secondary);
    background: var(--bg-muted);
    border-color: var(--border-default);
  }
  .automate-emails__empty {
    padding: 40px 20px;
    text-align: center;
    font-size: 0.875rem;
    color: var(--text-muted);
  }
  .automate-emails__pager {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    font-size: 0.8125rem;
    color: var(--text-muted);
  }

  /* ExecEdge funnel dashboard tone classes */
  .funnel-tone-indigo {
    color: var(--text-info);
    background: var(--info-light);
  }
  .funnel-tone-green {
    color: var(--text-success);
    background: var(--success-light);
  }
  .funnel-tone-orange {
    color: var(--text-warning);
    background: var(--warning-light);
  }
  .funnel-tone-purple {
    color: var(--interactive-primary);
    background: var(--info-light);
  }
  .funnel-tone-blue {
    color: var(--interactive-primary);
    background: var(--info-light);
  }

  /* HV Tracker / Association.jsx — pipeline chips & borders (light + dark via underlying tokens) */
  :root {
    --tracker-chip-fg: var(--primary-fg);
    --tracker-status-associated: var(--text-secondary);
    --tracker-chip-fg: var(--text-primary);
    --tracker-status-associated: var(--info);
    --tracker-status-agentic: var(--info);
    --tracker-status-pending: var(--warning);
    --tracker-status-phone: var(--info);
    --tracker-status-dl: var(--success);
    --tracker-status-partner: var(--secondary);
    --tracker-status-interview: var(--primary);
    --tracker-status-selected: var(--success);
    --tracker-status-rejected: var(--error);
    --tracker-status-hold: var(--text-muted);
    --tracker-status-fallback: var(--text-muted);
    --tracker-priority-p0: var(--success);
    --tracker-priority-p1: var(--info);
    --tracker-priority-p2: var(--warning);
    --tracker-priority-p3: var(--error);
    --tracker-priority-fallback: var(--border-strong);
    --sheet-priority-green-bg: #d4edbc;
    --sheet-priority-green-fg: #11734b;
    --sheet-priority-yellow-bg: #ffe5a0;
    --sheet-priority-yellow-fg: #473821;
    --sheet-priority-grey-bg: #e8eaed;
    --sheet-priority-grey-fg: #3c4043;
    --sheet-priority-red-bg: #b10202;
    --sheet-priority-red-fg: #ffcfc9;
    --sheet-priority-purple-bg: #e6cff2;
    --sheet-priority-purple-fg: #5a3286;
    --conversation-very-high: var(--success);
    --conversation-high: var(--info);
    --conversation-medium: var(--warning);
    --conversation-low: var(--error);
    --tracker-divider-fade: color-mix(in srgb, var(--text-primary) 8%, transparent);
    --tracker-snackbar-shadow: var(--shadow-lg);
    /* Small priority chips on pastel fills (card grid) */
    --tracker-priority-chip-fg: var(--text-primary);
    --tracker-execedge-fg: #142855;
    --tracker-execedge-accent: #fbbf24;
    --tracker-execedge-bg: #fbbf24;
    --tracker-execedge-border: #d97706;
  }
  .hv-priority-badge {
    background: var(--sheet-priority-grey-bg);
    color: var(--sheet-priority-grey-fg);
  }
  .hv-priority-badge.is-green {
    background: var(--sheet-priority-green-bg);
    color: var(--sheet-priority-green-fg);
  }
  .hv-priority-badge.is-yellow {
    background: var(--sheet-priority-yellow-bg);
    color: var(--sheet-priority-yellow-fg);
  }
  .hv-priority-badge.is-grey {
    background: var(--sheet-priority-grey-bg);
    color: var(--sheet-priority-grey-fg);
  }
  .hv-priority-badge.is-red {
    background: var(--sheet-priority-red-bg);
    color: var(--sheet-priority-red-fg);
  }
  .hv-priority-badge.is-purple {
    background: var(--sheet-priority-purple-bg);
    color: var(--sheet-priority-purple-fg);
  }
  .hv-confidence-badge {
    color: var(--text-muted);
    background: color-mix(in srgb, var(--text-muted) 16%, transparent);
  }
  .hv-confidence-badge.is-very-high {
    color: var(--conversation-very-high);
    background: color-mix(in srgb, var(--conversation-very-high) 16%, transparent);
  }
  .hv-confidence-badge.is-high {
    color: var(--conversation-high);
    background: color-mix(in srgb, var(--conversation-high) 16%, transparent);
  }
  .hv-confidence-badge.is-medium {
    color: var(--conversation-medium);
    background: color-mix(in srgb, var(--conversation-medium) 16%, transparent);
  }
  .hv-confidence-badge.is-low {
    color: var(--conversation-low);
    background: color-mix(in srgb, var(--conversation-low) 16%, transparent);
  }

  .dark,
  [data-theme="dark"] {
    --tracker-execedge-fg: #142855;
    --tracker-execedge-accent: #fbbf24;
    --tracker-execedge-bg: #fbbf24;
    --tracker-execedge-border: #fde68a;
  }

  /* HV Tracker pipeline — cards / table / kanban (Association.jsx) */
  .tracker-status-chip.MuiChip-root {
    --status-tone: var(--info);
    height: 22px !important;
    max-width: 100%;
    border: 1px solid color-mix(in srgb, var(--status-tone) 48%, transparent) !important;
    background: color-mix(in srgb, var(--status-tone) 18%, var(--bg-surface)) !important;
    color: var(--status-tone) !important;
    font-weight: 700 !important;
  }
  .tracker-status-chip .MuiChip-label {
    overflow: hidden;
    padding: 0 8px !important;
    color: inherit !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    letter-spacing: 0.01em;
  }

  .tracker-pipe-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 360px));
    gap: 1.25rem 1.2rem;
    justify-content: center;
    align-items: stretch;
  }
  .tracker-pipe-card {
    --pipe-accent: var(--primary);
    position: relative;
    display: flex;
    width: 100%;
    max-width: 360px;
    height: 100%;
    min-height: 0;
    flex-direction: column;
    overflow: hidden;
    cursor: pointer;
    color: var(--text-primary);
    border-radius: 16px !important;
    border: 1px solid color-mix(in srgb, var(--border-default) 92%, var(--teal));
    background:
      linear-gradient(
        180deg,
        color-mix(in srgb, var(--pipe-accent) 6%, var(--bg-surface)) 0%,
        var(--bg-surface) 42%
      ) !important;
    box-shadow: var(--shadow-sm) !important;
    transition:
      transform var(--transition-fast),
      box-shadow var(--transition-fast),
      border-color var(--transition-fast);
  }
  .tracker-pipe-card::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 0;
    width: 3px;
    background: var(--pipe-accent);
  }
  .tracker-pipe-card:hover {
    transform: translateY(-1px);
    border-color: color-mix(in srgb, var(--pipe-accent) 42%, var(--border-default));
    box-shadow: var(--shadow-sm) !important;
  }
  @media (hover: none) {
    .tracker-pipe-card:hover {
      transform: none;
    }
    .tracker-dash-cell:hover {
      transform: none;
    }
  }
  .tracker-pipe-card.is-selected {
    border-color: var(--primary);
    box-shadow: 0 0 0 1px var(--primary) !important;
  }
  .tracker-pipe-card-body {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 0.36rem;
    padding: 0.7rem 0.7rem 0.7rem calc(0.7rem + 3px) !important;
  }
  .tracker-pipe-card-body:last-child {
    padding-bottom: 0.7rem !important;
  }
  .tracker-pipe-card-enter {
    animation: tracker-card-in 0.38s ease-out both;
    animation-delay: var(--pipe-enter-delay, 0ms);
  }
  @keyframes tracker-card-in {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .tracker-pipe-card-enter {
      animation: none;
    }
  }
  @media (max-width: 767px) {
    .tracker-pipe-grid {
      grid-template-columns: minmax(0, 1fr);
      justify-items: center;
      gap: 0.9rem;
      width: 100%;
      max-width: 100%;
      margin-inline: auto;
      padding-inline: 0;
    }
    .tracker-pipe-card,
    .tracker-pipe-card.tracker-pipe-card-enter {
      width: 100%;
      max-width: min(100%, 420px);
      margin-inline: auto;
    }
    .tracker-pipe-board {
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
    }
    .tracker-pipe-column {
      scroll-snap-align: start;
      min-width: min(272px, calc(100vw - 48px));
      width: min(272px, calc(100vw - 48px));
    }
    .tracker-pipe-table {
      width: 100%;
      max-width: 100%;
    }
  }
  .tracker-status-pill {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    max-width: 8.25rem;
    height: 15px;
    padding: 0 6px;
    overflow: hidden;
    border: 1px solid color-mix(in srgb, var(--status-tone) 38%, transparent);
    border-radius: 999px;
    background: color-mix(in srgb, var(--status-tone) 12%, var(--bg-surface));
    color: var(--status-tone);
    font-size: 8.5px;
    font-weight: 700;
    letter-spacing: 0.02em;
    line-height: 1;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .tracker-execedge-chip {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    height: 16px;
    padding: 0 7px;
    border: 1px solid var(--tracker-execedge-border);
    border-radius: 999px;
    background: var(--tracker-execedge-bg);
    color: var(--tracker-execedge-fg);
    font-size: 8.5px;
    font-weight: 800;
    letter-spacing: 0.04em;
    line-height: 1;
    white-space: nowrap;
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--tracker-execedge-accent) 28%, transparent);
  }
  .tracker-pipe-select {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    cursor: pointer;
    border-radius: 999px;
    border: 1.5px solid var(--border-default);
    background: transparent;
    transition: border-color var(--transition-fast), background var(--transition-fast);
  }
  .tracker-pipe-select:hover {
    border-color: color-mix(in srgb, var(--primary) 55%, var(--border-default));
  }
  .tracker-pipe-select.is-on {
    border-color: var(--primary);
    background: var(--primary);
  }
  .tracker-pipe-metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    width: 100%;
    min-width: 0;
    margin-top: 0.12rem;
    padding: 0.32rem 0;
    border-radius: 10px;
    border: 1px solid color-mix(in srgb, var(--border-default) 82%, transparent);
    background: color-mix(in srgb, var(--bg-muted) 42%, var(--bg-surface));
  }
  .tracker-pipe-metrics.has-search {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .tracker-pipe-metric {
    display: flex;
    min-width: 0;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.08rem;
    padding: 0 0.4rem;
    text-align: center;
  }
  .tracker-pipe-metric + .tracker-pipe-metric {
    border-left: 1px solid color-mix(in srgb, var(--border-default) 80%, transparent);
  }
  .tracker-pipe-metric-name {
    display: inline-flex;
    align-items: center;
    gap: 0.22rem;
    max-width: 100%;
    overflow: hidden;
    color: var(--text-muted);
    font-size: 9.5px;
    font-weight: 650;
    letter-spacing: 0.01em;
    line-height: 1;
    white-space: nowrap;
  }
  .tracker-pipe-metric-name svg {
    flex-shrink: 0;
  }
  .tracker-pipe-metric b {
    display: block;
    overflow: hidden;
    max-width: 100%;
    font-size: 12.5px;
    font-weight: 750;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
    line-height: 1.15;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--text-primary);
  }
  .tracker-pipe-metric.is-exp .tracker-pipe-metric-name,
  .tracker-pipe-metric.is-exp svg { color: var(--teal); }
  .tracker-pipe-metric.is-notice .tracker-pipe-metric-name,
  .tracker-pipe-metric.is-notice svg { color: var(--warning); }
  .tracker-pipe-metric.is-ctc .tracker-pipe-metric-name,
  .tracker-pipe-metric.is-ctc svg { color: var(--info); }
  .tracker-pipe-metric.is-search .tracker-pipe-metric-name,
  .tracker-pipe-metric.is-search svg { color: var(--secondary); }
  .tracker-pipe-metric.is-exp b { color: var(--teal); }
  .tracker-pipe-metric.is-notice b { color: var(--warning); }
  .tracker-pipe-metric.is-ctc b { color: var(--info); }
  .tracker-pipe-metric.is-search b { color: var(--secondary); }
  .tracker-pipe-card-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-top: auto;
    padding-top: 0.5rem;
    border-top: 1px solid color-mix(in srgb, var(--border-default) 88%, transparent);
    color: var(--text-secondary);
    font-size: 11px;
    font-weight: 600;
  }
  .tracker-pipe-foot-main {
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: center;
    gap: 0.45rem;
  }
  .tracker-pipe-foot-sep {
    width: 1px;
    height: 12px;
    flex-shrink: 0;
    background: var(--border-default);
  }
  .tracker-execedge-mark {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 7px;
    border: 1px solid var(--tracker-execedge-border);
    background: var(--tracker-execedge-bg);
    color: var(--tracker-execedge-fg);
    font-size: 8px;
    font-weight: 800;
    letter-spacing: 0.04em;
    line-height: 1;
  }
  .tracker-execedge-mark.is-empty {
    visibility: hidden;
    border: 0;
    background: transparent;
  }
  .tracker-pipe-metric {
    font-variant-numeric: tabular-nums;
    color: var(--text-primary);
  }
  .tracker-pipe-dot {
    width: 3px;
    height: 3px;
    border-radius: 999px;
    background: var(--border-default);
    flex-shrink: 0;
  }
  .tracker-pipe-meta {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.28rem;
    color: var(--text-secondary);
  }
  .tracker-pipe-skill {
    height: 18px !important;
    max-width: 100%;
    border-radius: 999px !important;
    border: 1px solid color-mix(in srgb, var(--info) 26%, var(--border-default)) !important;
    background: color-mix(in srgb, var(--info) 9%, var(--bg-surface)) !important;
    color: var(--text-primary) !important;
    font-size: 9.5px !important;
    font-weight: 600 !important;
  }

  .tracker-pipe-board {
    display: flex;
    align-items: stretch;
    min-height: 0;
    gap: 0.7rem;
    overflow-x: auto;
    padding-bottom: 0.5rem;
    scrollbar-width: thin;
  }
  .tracker-pipe-board::-webkit-scrollbar { height: 8px; }
  .tracker-pipe-board::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: var(--border-default);
  }
  .tracker-pipe-column {
    --pipe-accent: var(--primary);
    display: flex;
    flex-direction: column;
    min-width: 272px;
    width: 272px;
    min-height: 100%;
    margin: 0;
    padding: 0;
    overflow: hidden;
    border-radius: 14px !important;
    border: 1px solid color-mix(in srgb, var(--pipe-accent) 28%, var(--border-default));
    background:
      linear-gradient(
        180deg,
        color-mix(in srgb, var(--pipe-accent) 10%, var(--bg-surface)) 0%,
        color-mix(in srgb, var(--bg-muted) 50%, var(--bg-surface)) 100%
      ) !important;
    box-shadow: var(--shadow-sm);
    transition: border-color var(--transition-fast), background var(--transition-fast);
  }
  .tracker-pipe-column.is-over {
    border-color: color-mix(in srgb, var(--pipe-accent) 58%, var(--border-default));
    background: color-mix(in srgb, var(--pipe-accent) 14%, var(--bg-surface)) !important;
  }
  .tracker-pipe-kanban-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.6rem 0.7rem 0.55rem;
    border-bottom: 1px solid color-mix(in srgb, var(--pipe-accent) 22%, var(--border-default));
    background: color-mix(in srgb, var(--bg-surface) 72%, transparent);
  }
  .tracker-pipe-kanban-title {
    min-width: 0;
    font-size: 12.5px;
    font-weight: 800;
    letter-spacing: -0.01em;
    color: var(--pipe-accent);
  }
  .tracker-pipe-kanban-count {
    flex-shrink: 0;
    min-width: 1.4rem;
    border-radius: 999px;
    border: 1px solid color-mix(in srgb, var(--pipe-accent) 35%, var(--border-default));
    background: color-mix(in srgb, var(--pipe-accent) 16%, var(--bg-surface));
    padding: 0.1rem 0.45rem;
    text-align: center;
    font-size: 11px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    color: var(--pipe-accent);
  }
  .tracker-pipe-kanban-list {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.55rem;
    min-height: 8rem;
    overflow-y: auto;
  }
  .tracker-pipe-kanban-empty {
    margin-top: 0.2rem;
    border: 1px dashed color-mix(in srgb, var(--pipe-accent) 28%, var(--border-default));
    border-radius: 10px;
    padding: 0.85rem 0.5rem;
    text-align: center;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
    background: color-mix(in srgb, var(--bg-surface) 55%, transparent);
  }
  .tracker-pipe-kanban-card {
    margin: 0;
    overflow: hidden;
    cursor: grab;
    border-radius: 12px !important;
    border: 1px solid color-mix(in srgb, var(--border-default) 85%, transparent) !important;
    border-left: 3px solid var(--pipe-accent, var(--primary)) !important;
    background: var(--bg-surface) !important;
    box-shadow: var(--shadow-sm) !important;
    transition: transform var(--transition-fast), box-shadow var(--transition-fast), border-color var(--transition-fast);
  }
  .tracker-pipe-kanban-card:hover {
    transform: translateY(-2px);
    border-color: color-mix(in srgb, var(--pipe-accent, var(--primary)) 42%, var(--border-default)) !important;
    box-shadow: var(--shadow-md) !important;
  }
  .tracker-pipe-kanban-card.is-dragging {
    transform: rotate(1.5deg);
    opacity: 0.92;
    box-shadow: var(--shadow-lg) !important;
  }
  .tracker-pipe-kanban-card .MuiCardContent-root {
    padding: 0.55rem 0.6rem 0.4rem !important;
  }
  .tracker-pipe-kanban-card .MuiCardActions-root {
    min-height: 0;
    padding: 0.15rem 0.35rem 0.3rem !important;
    border-top: 1px solid color-mix(in srgb, var(--border-default) 72%, transparent);
    background: color-mix(in srgb, var(--bg-muted) 35%, var(--bg-surface));
  }

  .tracker-pipe-table {
    overflow: auto;
    max-height: none;
    border-radius: 12px;
    border: 1px solid var(--border-default);
    background: var(--bg-surface);
    box-shadow: var(--shadow-sm);
  }
  .tracker-pipe-table .MuiTable-root {
    border-collapse: separate;
    border-spacing: 0;
  }
  .tracker-pipe-table .MuiTableCell-root {
    padding: 8px 11px !important;
    border-bottom: 1px solid var(--border-default) !important;
    border-right: 1px solid var(--border-default) !important;
    vertical-align: middle;
  }
  .tracker-pipe-table .MuiTableCell-root:last-child {
    border-right: 0 !important;
  }
  .tracker-pipe-table .MuiTableCell-head {
    position: sticky;
    top: 0;
    z-index: 3;
    white-space: nowrap;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--text-primary) !important;
    background: color-mix(in srgb, var(--bg-muted) 88%, var(--bg-surface)) !important;
    border-bottom: 1px solid var(--border-default) !important;
  }
  .tracker-pipe-table .MuiTableCell-body {
    font-size: 13px;
    color: var(--text-primary) !important;
    background: var(--bg-surface);
  }
  .tracker-pipe-table .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) .MuiTableCell-body {
    background: color-mix(in srgb, var(--bg-muted) 42%, var(--bg-surface));
  }
  .tracker-pipe-table .MuiTableRow-root {
    transition: background var(--transition-fast);
  }
  .tracker-pipe-table .MuiTableRow-hover:hover .MuiTableCell-body {
    background: color-mix(in srgb, var(--primary) 10%, var(--bg-surface)) !important;
  }
  .tracker-pipe-table .Mui-selected .MuiTableCell-body {
    background: color-mix(in srgb, var(--primary) 14%, var(--bg-surface)) !important;
  }
  .tracker-pipe-table .Mui-selected:hover .MuiTableCell-body {
    background: color-mix(in srgb, var(--primary) 18%, var(--bg-surface)) !important;
  }
  .tracker-pipe-status-btn {
    margin: 0.12rem 0.25rem 0.12rem 0 !important;
    min-width: 0 !important;
    padding: 0.12rem 0.5rem !important;
    border-radius: 999px !important;
    border-color: color-mix(in srgb, var(--primary) 35%, var(--border-default)) !important;
    color: var(--text-primary) !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    text-transform: none !important;
    background: color-mix(in srgb, var(--primary) 8%, var(--bg-surface)) !important;
  }
  .tracker-pipe-status-btn:hover {
    border-color: var(--primary) !important;
    background: color-mix(in srgb, var(--primary) 16%, var(--bg-surface)) !important;
  }

  /* HV Tracker candidate modal (CandidateModal.jsx) */
  .cm-modal-root .MuiBackdrop-root {
    background: color-mix(in srgb, var(--bg-page) 28%, black) !important;
    backdrop-filter: blur(10px);
  }

  .cm-modal {
    position: relative;
    color: var(--text-primary);
    background: var(--bg-surface) !important;
    border: 1px solid color-mix(in srgb, var(--teal) 28%, var(--border-default)) !important;
    box-shadow: var(--shadow-popover) !important;
    border-radius: 20px !important;
  }
  .cm-modal::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 6;
    height: 5px;
    background: linear-gradient(90deg, var(--primary) 0%, var(--teal) 58%, var(--teal-2) 100%);
  }

  .cm-sidebar {
    color: var(--text-primary);
    background: var(--bg-surface) !important;
    border-right: 1px solid color-mix(in srgb, var(--teal) 22%, var(--border-default)) !important;
    padding-top: 1.15rem !important;
  }
  .cm-sidebar .MuiTypography-root {
    color: var(--text-primary);
  }

  .cm-modal .cm-avatar.MuiAvatar-root,
  .cm-modal .MuiAvatar-root {
    width: 60px !important;
    height: 60px !important;
    border: 2px solid var(--teal) !important;
    box-shadow: 0 0 0 4px color-mix(in srgb, var(--teal) 20%, transparent);
    background: color-mix(in srgb, var(--primary) 88%, var(--bg-surface)) !important;
    color: var(--text-inverse) !important;
    font-weight: 800;
  }
  html[data-theme="dark"] .cm-modal .MuiAvatar-root,
  html.dark .cm-modal .MuiAvatar-root {
    color: #001410 !important;
    background: var(--teal) !important;
  }

  .cm-hero .MuiTypography-root:first-of-type {
    color: var(--text-primary) !important;
    font-size: 1.05rem !important;
    font-weight: 800 !important;
    letter-spacing: -0.02em;
  }
  .cm-hero .MuiTypography-body2 {
    color: var(--text-secondary) !important;
  }

  .cm-contact .MuiIconButton-root {
    width: 34px;
    height: 34px;
    border-radius: 10px !important;
    border: 1px solid color-mix(in srgb, var(--teal) 28%, var(--border-default)) !important;
    background: color-mix(in srgb, var(--teal) 10%, var(--bg-surface)) !important;
    color: var(--primary) !important;
  }
  .cm-contact .MuiIconButton-root:hover {
    background: color-mix(in srgb, var(--teal) 22%, var(--bg-surface)) !important;
    border-color: var(--teal) !important;
    color: var(--teal) !important;
  }

  .cm-stat {
    padding: 0.15rem 0.2rem 0.15rem 0;
    text-align: left;
    background: transparent;
    border: 0;
    border-radius: 0;
  }
  .cm-stat-wide {
    grid-column: 1 / -1;
  }
  .cm-stat-label {
    display: block;
    margin-bottom: 2px;
    color: var(--text-muted) !important;
    font-size: 10px !important;
    font-weight: 800 !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .cm-stat-value {
    color: var(--text-primary) !important;
    font-size: 13px !important;
    font-weight: 800 !important;
    line-height: 1.2;
  }

  .cm-status,
  .hv-status-top {
    display: inline-flex;
    max-width: 100%;
    align-items: center;
    justify-content: flex-start;
    height: 18px;
    padding: 0 8px !important;
    border-radius: 999px !important;
    border: 1px solid color-mix(in srgb, var(--status-tone) 42%, transparent) !important;
    background: color-mix(in srgb, var(--status-tone) 14%, var(--bg-surface)) !important;
  }
  .cm-status .MuiTypography-root,
  .hv-status-top .MuiTypography-root {
    color: var(--status-tone) !important;
    font-size: 9px !important;
    font-weight: 800 !important;
    letter-spacing: 0.04em;
    line-height: 1 !important;
  }

  .cm-main {
    background: var(--bg-surface) !important;
  }

  .cm-tabs {
    background: var(--bg-surface) !important;
    border-bottom: 1px solid color-mix(in srgb, var(--teal) 22%, var(--border-default)) !important;
    padding-top: 10px !important;
    padding-bottom: 8px !important;
  }
  .cm-modal .MuiTab-root {
    min-height: 36px !important;
    margin: 0 3px !important;
    padding: 6px 14px !important;
    border-radius: 999px !important;
    color: var(--text-secondary) !important;
    font-size: 13px !important;
    font-weight: 700 !important;
    text-transform: none !important;
  }
  .cm-modal .MuiTab-root:hover {
    background: color-mix(in srgb, var(--teal) 12%, transparent) !important;
    color: var(--text-primary) !important;
  }
  .cm-modal .MuiTab-root.Mui-selected {
    color: var(--text-primary) !important;
    background: color-mix(in srgb, var(--teal) 18%, var(--bg-surface)) !important;
    box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--teal) 45%, transparent);
  }
  .cm-modal .MuiTabs-indicator {
    display: none !important;
  }
  .cm-tabs .MuiIconButton-root {
    border-radius: 10px !important;
    color: var(--text-primary) !important;
  }
  .cm-tabs .MuiIconButton-root:hover {
    background: color-mix(in srgb, var(--teal) 16%, transparent) !important;
  }

  .cm-pane {
    color: var(--text-primary);
  }
  .cm-pane > .MuiTypography-h6,
  .cm-pane .MuiTypography-h6 {
    color: var(--text-primary) !important;
    font-weight: 800 !important;
    letter-spacing: -0.02em;
  }

  .cm-modal .MuiCard-root,
  .cm-section {
    color: var(--text-primary);
    background: transparent !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
  .cm-section {
    margin-bottom: 1.35rem;
    padding-bottom: 0.15rem;
    border: 0 !important;
  }
  .cm-section:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }
  .cm-section-head {
    background: transparent !important;
    border: 0 !important;
    border-radius: 0 !important;
    padding: 0.15rem 0 0.45rem !important;
    border-bottom: 1px solid var(--border-default) !important;
  }
  .cm-section-head .MuiTypography-root {
    color: var(--text-primary) !important;
    font-weight: 800 !important;
  }
  .cm-section-head svg {
    color: var(--teal) !important;
  }

  .hv-profile-block {
    margin-bottom: 12px;
    padding: 12px 12px 10px;
    border: 1px solid color-mix(in srgb, var(--border-default) 88%, transparent);
    border-radius: 14px;
    background: color-mix(in srgb, var(--bg-muted) 35%, var(--bg-surface));
  }
  .hv-profile-block:last-child {
    margin-bottom: 0;
  }
  .hv-profile-head {
    min-height: 32px;
    margin-bottom: 8px !important;
    padding: 0 0 8px !important;
    border-bottom: 1px solid color-mix(in srgb, var(--border-default) 80%, transparent);
  }
  .hv-profile-title svg {
    width: 26px;
    height: 26px;
    padding: 5px;
    border-radius: 8px;
    background: color-mix(in srgb, var(--teal) 14%, transparent);
    color: var(--teal) !important;
  }
  .hv-fields {
    gap: 0;
    overflow: hidden;
    border: 1px solid color-mix(in srgb, var(--border-default) 85%, transparent);
    border-radius: 10px;
    background: var(--bg-surface);
  }
  .hv-field {
    min-width: 0;
    padding: 10px 12px;
    border-bottom: 1px solid color-mix(in srgb, var(--border-default) 85%, transparent);
  }
  .hv-field:last-child {
    border-bottom: 0;
  }
  @media (min-width: 600px) {
    .hv-field:nth-last-child(2):nth-child(odd) {
      border-bottom: 0;
    }
    .hv-field:nth-child(odd) {
      padding-right: 14px;
      border-right: 1px solid color-mix(in srgb, var(--border-default) 85%, transparent);
    }
    .hv-field:nth-child(even) {
      padding-left: 14px;
    }
  }
  .hv-field-k {
    display: block;
    margin-bottom: 2px;
    color: var(--text-muted) !important;
    font-size: 9.5px !important;
    font-weight: 800 !important;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    line-height: 1.2;
  }
  .hv-field-v {
    min-height: 1.2em;
    line-height: 1.35;
  }

  .hv-resume-row {
    padding: 8px 10px;
    border: 1px solid color-mix(in srgb, var(--teal) 22%, var(--border-default));
    border-left: 3px solid var(--teal);
    border-radius: 10px;
    background: color-mix(in srgb, var(--teal) 7%, var(--bg-surface));
  }

  .cm-personal-row {
    background: transparent;
    border: 0;
  }
  .cm-personal-row:hover {
    background: transparent;
  }
  .hv-field:hover {
    background: color-mix(in srgb, var(--teal) 5%, transparent);
  }
  .hv-mandatory-banner {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 10px;
    padding: 8px 10px;
    border-radius: 10px;
    border: 1px solid color-mix(in srgb, var(--error) 38%, transparent);
    background: color-mix(in srgb, var(--error) 12%, var(--bg-surface));
    color: var(--error);
    font-size: 12px;
    font-weight: 650;
    line-height: 1.35;
  }
  .hv-mandatory-banner svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
  .hv-mandatory-banner em {
    display: block;
    margin-top: 1px;
    font-style: normal;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.02em;
    opacity: 0.85;
  }
  .hv-field-req {
    margin-left: 3px;
    color: var(--error);
    font-weight: 800;
  }
  .hv-field.is-error,
  .cm-personal-row.is-error {
    position: relative;
    padding: 10px 12px 10px 14px;
    border-radius: 8px;
    background: color-mix(in srgb, var(--error) 10%, var(--bg-surface));
    box-shadow: inset 3px 0 0 var(--error);
  }
  .hv-field.is-error:hover,
  .cm-personal-row.is-error:hover {
    background: color-mix(in srgb, var(--error) 14%, var(--bg-surface));
  }
  .hv-field-error {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
    padding: 2px 8px;
    border-radius: 999px;
    background: color-mix(in srgb, var(--error) 16%, transparent);
    color: var(--error) !important;
    font-size: 10px !important;
    font-weight: 700 !important;
    letter-spacing: 0.02em;
    line-height: 1.2;
  }
  .cm-personal-row .MuiTypography-root,
  .cm-personal-row {
    color: var(--text-primary);
  }

  .cm-entry {
    color: var(--text-primary);
    background: transparent !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none;
    border-bottom: 1px solid var(--border-default) !important;
  }
  .cm-entry .MuiTypography-root {
    color: var(--text-primary);
  }
  .cm-entry-edu {
    border-left-color: transparent !important;
    border-color: var(--border-default) !important;
    background: transparent !important;
  }
  .cm-entry-exp {
    background: transparent !important;
  }

  .cm-skill {
    color: var(--text-primary) !important;
    background: color-mix(in srgb, var(--teal) 10%, var(--bg-subtle)) !important;
    border: 1px solid color-mix(in srgb, var(--teal) 28%, var(--border-default)) !important;
  }

  .cm-edit-form {
    margin: 0 0 10px !important;
    padding: 12px !important;
    background: var(--bg-surface) !important;
    border: 1px solid color-mix(in srgb, var(--border-default) 88%, transparent) !important;
    border-radius: 12px !important;
  }
  .cm-modal .cm-btn-add.MuiButton-root,
  .cm-modal .cm-btn-save.MuiButton-root,
  .cm-modal .cm-btn-cancel.MuiButton-root,
  .cm-modal .cm-btn-present.MuiButton-root {
    min-width: 0 !important;
    min-height: 26px !important;
    height: 26px !important;
    padding: 0 10px !important;
    border-radius: 999px !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    line-height: 1 !important;
    text-transform: none !important;
    box-shadow: none !important;
  }
  .cm-modal .cm-btn-add.MuiButton-root {
    color: var(--success) !important;
    border: 1px solid color-mix(in srgb, var(--success) 40%, transparent) !important;
    background: color-mix(in srgb, var(--success) 8%, var(--bg-surface)) !important;
  }
  .cm-modal .cm-btn-add--info.MuiButton-root {
    color: var(--info) !important;
    border-color: color-mix(in srgb, var(--info) 40%, transparent) !important;
    background: color-mix(in srgb, var(--info) 8%, var(--bg-surface)) !important;
  }
  .cm-modal .cm-btn-save.MuiButton-root {
    color: var(--text-inverse) !important;
    background: var(--success) !important;
  }
  .cm-modal .cm-btn-cancel.MuiButton-root {
    color: var(--text-secondary) !important;
    border: 1px solid var(--border-default) !important;
    background: transparent !important;
  }
  .cm-modal .cm-btn-add .MuiButton-startIcon,
  .cm-modal .cm-btn-save .MuiButton-startIcon,
  .cm-modal .cm-btn-cancel .MuiButton-startIcon {
    margin-left: 0 !important;
    margin-right: 4px !important;
  }
  .cm-modal .cm-btn-add .MuiButton-startIcon svg,
  .cm-modal .cm-btn-save .MuiButton-startIcon svg,
  .cm-modal .cm-btn-cancel .MuiButton-startIcon svg {
    font-size: 12px;
  }

  .cm-modal .MuiDivider-root {
    border-color: color-mix(in srgb, var(--teal) 18%, var(--border-default)) !important;
  }
  .cm-modal .MuiOutlinedInput-root,
  .cm-dialog .MuiOutlinedInput-root {
    background: var(--control-bg) !important;
    border-radius: 10px !important;
    color: var(--text-primary) !important;
  }
  .cm-modal .MuiInputBase-input,
  .cm-modal .MuiSelect-select,
  .cm-modal .MuiAutocomplete-input,
  .cm-dialog .MuiInputBase-input {
    color: var(--text-primary) !important;
    -webkit-text-fill-color: var(--text-primary) !important;
  }
  .cm-modal .MuiInputBase-input::placeholder,
  .cm-dialog .MuiInputBase-input::placeholder {
    color: var(--text-muted) !important;
    opacity: 1 !important;
  }
  .cm-modal .MuiOutlinedInput-notchedOutline,
  .cm-dialog .MuiOutlinedInput-notchedOutline {
    border-color: var(--border-default) !important;
  }
  .cm-modal .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline,
  .cm-dialog .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: var(--teal) !important;
  }
  .cm-modal .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline,
  .cm-dialog .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: var(--primary) !important;
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--teal) 18%, transparent);
  }
  .cm-modal .MuiSelect-icon,
  .cm-modal .MuiAutocomplete-endAdornment .MuiSvgIcon-root,
  .cm-modal .MuiNativeSelect-icon {
    color: var(--text-secondary) !important;
  }
  .cm-modal .MuiIconButton-root.Mui-disabled {
    opacity: 0.35;
  }
  .cm-modal .MuiChip-root {
    font-weight: 700 !important;
  }
  .cm-modal .MuiChip-label {
    color: inherit !important;
  }
  .cm-modal .cm-priority-chip.MuiChip-root,
  .cm-modal .cm-priority-chip .MuiChip-label {
    color: var(--text-inverse) !important;
  }

  .cm-modal .MuiList-root,
  .cm-modal .MuiListItem-root,
  .cm-modal .MuiListItemText-primary,
  .cm-modal .MuiListItemText-secondary {
    color: var(--text-primary);
  }
  .cm-modal .MuiListItemText-secondary,
  .cm-modal .MuiListItemText-secondary .MuiTypography-root {
    color: var(--text-secondary) !important;
  }
  .cm-modal .MuiLinearProgress-root {
    background: color-mix(in srgb, var(--teal) 18%, var(--bg-subtle)) !important;
    border-radius: 99px;
  }
  .cm-modal .MuiLinearProgress-bar {
    background: var(--teal) !important;
  }

  .cm-dialog.MuiPaper-root {
    overflow: hidden;
    color: var(--text-primary);
    background: var(--bg-surface) !important;
    border: 1px solid color-mix(in srgb, var(--teal) 28%, var(--border-default)) !important;
    border-radius: 16px !important;
    box-shadow: var(--shadow-popover) !important;
  }
  .cm-dialog .MuiDialogTitle-root,
  .cm-dialog .MuiDialogTitle-root .MuiTypography-root {
    color: var(--text-primary) !important;
    font-weight: 800 !important;
  }
  .cm-dialog .MuiDialogTitle-root {
    padding: 14px 20px !important;
    background: color-mix(in srgb, var(--teal) 10%, var(--bg-surface)) !important;
    border-bottom: 1px solid color-mix(in srgb, var(--teal) 18%, var(--border-default));
  }
  .cm-dialog .MuiDialogContent-root,
  .cm-dialog .MuiDialogContent-root .MuiTypography-root {
    color: var(--text-primary);
  }
  .cm-dialog .MuiDialogActions-root {
    padding: 12px 20px 16px !important;
    background: var(--bg-page);
  }

  .cm-entry .MuiTypography-root:first-of-type {
    color: var(--text-primary) !important;
    font-weight: 700;
  }
  .cm-sidebar .MuiTypography-caption {
    color: var(--text-muted) !important;
  }
  .cm-modal .MuiButton-outlined {
    color: var(--text-primary);
    border-color: color-mix(in srgb, var(--teal) 35%, var(--border-default));
  }
  .cm-modal .MuiList-root {
    background: var(--bg-surface);
    border-radius: 14px;
  }
  .cm-modal .MuiListItem-root:hover {
    background: color-mix(in srgb, var(--teal) 8%, var(--bg-subtle));
  }
  .cm-modal [class*="emptyState"],
  .cm-modal [class*="dropZone"] {
    color: var(--text-secondary);
    background: color-mix(in srgb, var(--teal) 6%, var(--bg-page)) !important;
    border-color: color-mix(in srgb, var(--teal) 28%, var(--border-default)) !important;
  }

  @media (max-width: 767px) {
    .cm-modal-root {
      padding: 0 !important;
      align-items: stretch;
    }
    .cm-modal {
      width: 100% !important;
      max-width: 100% !important;
      height: 100dvh !important;
      max-height: 100dvh !important;
      border-radius: 0 !important;
      border-left: 0 !important;
      border-right: 0 !important;
    }
    .cm-layout {
      min-height: 0;
    }
    .cm-sidebar {
      border-right: 0 !important;
      border-bottom: 1px solid color-mix(in srgb, var(--teal) 18%, var(--border-default)) !important;
      padding: 0.65rem 0.75rem !important;
      max-height: min(42vh, 340px);
    }
    .hv-mobile-fields {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .hv-mobile-pair {
      display: grid;
      grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
      gap: 8px;
      align-items: end;
    }
    .hv-mobile-field {
      min-width: 0;
    }
    .hv-mobile-field .MuiOutlinedInput-root,
    .hv-mobile-field .MuiInputBase-root {
      min-height: 34px;
    }
    .hv-mobile-field .MuiSelect-select,
    .hv-mobile-field .MuiInputBase-input {
      padding-top: 6px !important;
      padding-bottom: 6px !important;
      font-size: 12px !important;
    }
    .hv-mobile-field .MuiButton-root {
      min-height: 34px !important;
      height: 34px !important;
      padding: 0 8px !important;
      font-size: 11px !important;
      line-height: 1.1 !important;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .cm-main {
      min-height: 0;
      flex: 1 1 auto;
    }
    .cm-tabs .MuiTabs-flexContainer {
      flex-wrap: nowrap;
    }
    .cm-modal .MuiTab-root {
      min-width: auto !important;
      margin: 0 1px !important;
      padding: 6px 10px !important;
      font-size: 12px !important;
      flex-shrink: 0;
      white-space: nowrap;
    }
    .hv-profile-head {
      align-items: flex-start;
    }
    .hv-date-pill {
      max-width: 100%;
      white-space: normal;
    }
    .hv-mail-card {
      align-items: flex-start;
    }
    .hv-mail-detail {
      padding-top: 8px;
      padding-right: 40px;
    }
    .hv-mail-close {
      top: 2px;
      right: 2px;
      width: 34px !important;
      height: 34px !important;
    }
    .hv-remark-editor {
      padding: 10px;
    }
    .hv-remark-title {
      padding-right: 40px;
    }
    .hv-remark-close {
      top: 2px;
      right: 2px;
      width: 34px !important;
      height: 34px !important;
    }
    .hv-remark-actions {
      justify-content: stretch;
    }
    .hv-remark-actions .MuiButton-root {
      flex: 1 1 0;
    }
    .cm-tabs {
      overflow-x: auto;
      padding-top: 6px !important;
      padding-bottom: 6px !important;
      scrollbar-width: none;
    }
    .cm-tabs::-webkit-scrollbar {
      display: none;
    }
    .cm-pane {
      padding: 0.75rem !important;
    }
    .cm-modal .MuiAvatar-root {
      width: 52px !important;
      height: 52px !important;
    }
    .cm-stat {
      text-align: left;
      padding: 0.35rem 0.2rem;
    }
  }

  .tracker-dash-cell {
    display: flex;
    height: 32px;
    min-width: 44px;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    border: 1px solid color-mix(in srgb, var(--cell-color) 22%, transparent);
    font-family: var(--font-mono, ui-monospace);
    font-size: 12px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
    text-shadow: 0 0 8px var(--bg-surface), 0 0 2px var(--bg-surface);
    transition:
      transform var(--transition-fast),
      box-shadow var(--transition-fast),
      border-color var(--transition-fast);
  }
  .tracker-dash-cell.is-empty {
    border-color: transparent;
    text-shadow: none;
  }
  .tracker-dash-cell.is-filled {
    box-shadow:
      inset 0 1px 0 color-mix(in srgb, white 10%, transparent),
      0 1px 2px color-mix(in srgb, var(--cell-color) 16%, transparent);
  }
  .tracker-dash-cell:hover {
    transform: translateY(-1px);
    border-color: color-mix(in srgb, var(--cell-color) 55%, var(--border-default));
  }
  .tracker-dash-cell.is-active {
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--text-primary) 35%, transparent);
  }

  .tracker-dash-tip {
    --tip-tone: var(--primary);
    min-width: 0;
    max-width: min(220px, calc(100vw - 16px));
    padding: 6px 8px 7px;
    overflow: hidden;
    border: 1px solid color-mix(in srgb, var(--tip-tone) 38%, var(--border-default));
    border-radius: 10px;
    background: color-mix(in srgb, var(--bg-surface) 92%, transparent);
    box-shadow:
      0 8px 22px color-mix(in srgb, black 28%, transparent),
      inset 3px 0 0 var(--tip-tone);
    backdrop-filter: blur(10px);
  }
  .tracker-dash-tip-kicker {
    max-width: 100%;
    margin-bottom: 3px;
    overflow: hidden;
    color: var(--text-muted);
    font-size: 9px;
    font-weight: 650;
    letter-spacing: 0.04em;
    line-height: 1.25;
    text-overflow: ellipsis;
    text-transform: uppercase;
    white-space: nowrap;
  }
  .tracker-dash-tip-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  .tracker-dash-tip-stage {
    display: inline-flex;
    min-width: 0;
    align-items: center;
    gap: 5px;
    overflow: hidden;
    color: var(--text-primary);
    font-size: 11px;
    font-weight: 750;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .tracker-dash-tip-stage i,
  .tracker-dash-tip-pill i {
    display: block;
    width: 6px;
    height: 6px;
    flex-shrink: 0;
    border-radius: 999px;
  }
  .tracker-dash-tip-head b {
    flex-shrink: 0;
    min-width: 1.1rem;
    padding: 0 5px;
    border-radius: 999px;
    background: color-mix(in srgb, var(--tip-tone) 18%, transparent);
    color: var(--tip-tone);
    font-family: var(--font-mono, ui-monospace);
    font-size: 10px;
    font-weight: 800;
    font-style: normal;
    font-variant-numeric: tabular-nums;
    line-height: 1.5;
    text-align: center;
  }
  .tracker-dash-tip-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    margin-top: 6px;
  }
  .tracker-dash-tip-pill {
    display: inline-flex;
    max-width: 100%;
    align-items: center;
    gap: 4px;
    padding: 2px 6px 2px 5px;
    border: 1px solid color-mix(in srgb, var(--pill-tone) 28%, transparent);
    border-radius: 999px;
    background: color-mix(in srgb, var(--pill-tone) 12%, var(--bg-surface));
    color: var(--text-secondary);
    font-size: 9.5px;
    font-weight: 650;
    line-height: 1.3;
    white-space: nowrap;
  }
  .tracker-dash-tip-pill em {
    color: var(--pill-tone);
    font-family: var(--font-mono, ui-monospace);
    font-size: 9.5px;
    font-style: normal;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }

  .hv-id-card {
    padding: 0;
    text-align: left;
    background: transparent;
    border: 0;
    border-radius: 0;
  }
  .hv-id-block {
    padding: 8px 8px 9px;
    border: 1px solid color-mix(in srgb, var(--teal) 18%, var(--border-default));
    border-radius: 12px;
    background: color-mix(in srgb, var(--teal) 6%, var(--bg-surface));
  }
  .cm-modal .hv-id-avatar.MuiAvatar-root {
    width: 40px !important;
    height: 40px !important;
    border: 2px solid var(--teal) !important;
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--teal) 16%, transparent);
  }
  .hv-contact-grid {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .hv-contact-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    padding: 0;
    border: 1px solid transparent;
    border-radius: 8px;
    background: transparent;
    color: var(--text-secondary);
    cursor: pointer;
    transition: background var(--transition-fast), border-color var(--transition-fast), color var(--transition-fast);
  }
  .hv-contact-btn svg {
    width: 13px;
    height: 13px;
  }
  .hv-contact-btn--call {
    color: var(--teal);
    background: color-mix(in srgb, var(--teal) 14%, transparent);
    border-color: color-mix(in srgb, var(--teal) 32%, transparent);
  }
  .hv-contact-btn--wa {
    color: var(--success);
    background: color-mix(in srgb, var(--success) 14%, transparent);
    border-color: color-mix(in srgb, var(--success) 32%, transparent);
  }
  .hv-contact-btn--in {
    color: var(--info);
    background: color-mix(in srgb, var(--info) 14%, transparent);
    border-color: color-mix(in srgb, var(--info) 32%, transparent);
  }
  .hv-contact-btn--mail {
    color: var(--error);
    background: color-mix(in srgb, var(--error) 14%, transparent);
    border-color: color-mix(in srgb, var(--error) 32%, transparent);
  }
  .hv-contact-btn:hover {
    filter: brightness(1.08);
  }
  .hv-contact-btn--wa:hover { color: var(--success); }
  .hv-contact-btn--in:hover { color: var(--info); }
  .hv-contact-btn--mail:hover { color: var(--error); }

  .hv-role-block {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 0;
    background: transparent;
    border: 0;
    border-radius: 0;
  }
  .hv-role-k {
    color: var(--text-muted);
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .hv-role-v {
    color: var(--text-primary) !important;
    font-size: 12.5px !important;
    font-weight: 700 !important;
    line-height: 1.3 !important;
    overflow-wrap: anywhere;
  }
  .hv-role-status {
    max-width: 100%;
  }

  .hv-metric-strip {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    overflow: hidden;
    border-radius: 10px;
    background: color-mix(in srgb, var(--teal) 8%, var(--bg-subtle));
  }
  .hv-metric {
    position: relative;
    min-width: 0;
    padding: 8px 8px 8px 10px;
    border-right: 1px solid color-mix(in srgb, var(--border-default) 88%, transparent);
  }
  .hv-metric:last-child { border-right: 0; }
  .hv-metric::before {
    content: "";
    position: absolute;
    top: 8px;
    bottom: 8px;
    left: 0;
    width: 2px;
    border-radius: 2px;
    background: var(--teal);
  }
  .hv-metric--exp::before { background: var(--warning); }
  .hv-metric--notice::before { background: var(--info); }
  .hv-metric-k {
    display: block;
    margin-bottom: 2px;
    color: var(--text-muted);
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .hv-metric-v {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--text-primary);
    font-size: 12.5px;
    font-weight: 800;
    line-height: 1.2;
  }

  @media (max-width: 767px) {
    .cm-sidebar .hv-role-block {
      display: flex !important;
      flex-direction: row !important;
      flex-wrap: nowrap;
      align-items: center !important;
      justify-content: space-between;
      gap: 8px;
    }
    .cm-sidebar .hv-role-k {
      display: none !important;
    }
    .cm-sidebar .hv-role-v {
      flex: 1 1 auto;
      min-width: 0;
      overflow: hidden !important;
      text-overflow: ellipsis;
      white-space: nowrap !important;
      font-size: 12px !important;
    }
    .cm-sidebar .hv-role-status,
    .cm-sidebar .hv-role-status.cm-status {
      flex: 0 0 auto;
      width: auto;
      max-width: none;
      height: 20px;
      padding: 0 8px !important;
    }
    .cm-sidebar .hv-metric-strip {
      overflow: visible;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
    .cm-sidebar .hv-metric {
      padding: 6px 4px 6px 8px;
    }
    .cm-sidebar .hv-metric::before {
      top: 6px;
      bottom: 6px;
    }
    .cm-sidebar .hv-metric-k {
      margin-bottom: 3px;
      font-size: 8px;
      letter-spacing: 0.04em;
    }
    .cm-sidebar .hv-metric-v {
      overflow: visible;
      text-overflow: unset;
      white-space: nowrap;
      font-size: 12px;
    }
  }
  .hv-role-pill {
    padding: 6px 8px;
    border-radius: 8px;
    background: color-mix(in srgb, var(--primary) 10%, var(--bg-subtle));
    border: 1px solid color-mix(in srgb, var(--primary) 22%, var(--border-default));
    color: var(--text-primary);
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    line-height: 1.3;
  }

  .hv-roles-page {
    min-height: calc(100dvh - 5rem);
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
    padding: 16px 0 28px;
    background: var(--bg-page);
  }
  .hv-roles-shell {
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    align-items: stretch;
    gap: 16px;
    width: 100%;
    max-width: 1400px;
    min-width: 0;
    margin: 0 auto;
    padding-left: 16px;
    padding-right: 16px;
  }
  @media (min-width: 768px) {
    .hv-roles-shell {
      padding-left: 24px;
      padding-right: 24px;
    }
  }
  .hv-roles-aside {
    display: flex;
    min-width: 0;
    min-height: 100%;
    max-height: calc(100dvh - 6.5rem);
    overflow: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .hv-roles-aside::-webkit-scrollbar,
  .hv-roles-rail::-webkit-scrollbar,
  .hv-roles-drawer-panel::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
  .hv-roles-rail,
  .hv-roles-main,
  .hv-roles-panel,
  .hv-roles-bulk,
  .hv-roles-table-wrap,
  .hv-roles-card,
  .hv-roles-empty {
    border: 1px solid color-mix(in srgb, var(--teal) 18%, var(--border-default));
    background: var(--bg-surface);
    box-shadow: var(--shadow-sm);
  }
  .hv-roles-rail {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    width: 100%;
    min-height: 100%;
    gap: 12px;
    padding: 14px;
    border-radius: 16px;
    overflow: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .hv-roles-brand {
    display: flex;
    gap: 10px;
    align-items: flex-start;
  }
  .hv-roles-brand-mark {
    display: inline-flex;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    color: var(--teal);
    background: color-mix(in srgb, var(--teal) 14%, transparent);
    border: 1px solid color-mix(in srgb, var(--teal) 28%, transparent);
  }
  .hv-roles-brand h1 {
    margin: 0;
    font-size: 17px;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: var(--text-primary);
  }
  .hv-roles-brand p {
    margin: 2px 0 0;
    font-size: 11px;
    line-height: 1.35;
    color: var(--text-muted);
  }
  .hv-roles-rail-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
  .hv-roles-metrics {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
  .hv-roles-metric {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 0;
    padding: 7px 9px;
    border-radius: 10px;
    background: var(--bg-subtle);
    border: 1px solid var(--border-subtle);
  }
  .hv-roles-metric span {
    display: block;
    margin-bottom: 1px;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--text-muted);
  }
  .hv-roles-metric strong {
    font-size: 15px;
    font-weight: 800;
    line-height: 1.15;
    color: var(--text-primary);
  }
  .hv-roles-metric--teal { box-shadow: inset 3px 0 0 var(--teal); }
  .hv-roles-metric--success { box-shadow: inset 3px 0 0 var(--success); }
  .hv-roles-metric--info { box-shadow: inset 3px 0 0 var(--info); }
  .hv-roles-metric--warning { box-shadow: inset 3px 0 0 var(--warning); }
  .hv-roles-main {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 100%;
    padding: 16px;
    border-radius: 16px;
  }
  .hv-roles-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 10px;
    position: relative;
    z-index: 3;
    overflow: visible;
  }
  .hv-roles-search {
    display: flex;
    min-width: 0;
    flex: 1 1 220px;
    align-items: center;
    gap: 8px;
    padding: 0 10px;
    border-radius: 12px;
    border: 1px solid var(--border-default);
    background: var(--control-bg);
    color: var(--text-muted);
  }
  .hv-roles-toolbar-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }
  .hv-roles-menu {
    position: relative;
  }
  .hv-roles-menu-panel {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    left: auto;
    z-index: 40;
    width: min(220px, calc(100vw - 24px));
    max-height: 240px;
    overflow: auto;
    padding: 8px;
    border-radius: 12px;
    border: 1px solid color-mix(in srgb, var(--teal) 22%, var(--border-default));
    background: var(--bg-surface);
    box-shadow: var(--shadow-popover);
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .hv-roles-menu-panel::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
  .hv-roles-menu-panel.is-end {
    left: auto;
    right: 0;
  }
  .hv-roles-menu-panel h3 {
    margin: 0 0 6px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--text-muted);
  }
  .hv-roles-filter-section {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 10px;
    padding: 10px;
    border-radius: 12px;
    border: 1px solid var(--border-default);
    background: var(--bg-subtle);
  }
  .hv-roles-filters {
    display: grid;
    gap: 10px;
  }
  .hv-roles-filter {
    position: relative;
    display: grid;
    gap: 6px;
    min-width: 0;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--text-muted);
  }
  .hv-roles-filter-btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    height: 36px;
    padding: 0 10px;
    border: 1px solid var(--border-default);
    border-radius: 10px;
    background: var(--bg-surface);
    color: var(--text-primary);
    font-size: 13px;
    font-weight: 600;
    text-align: left;
    cursor: pointer;
  }
  .hv-roles-filter-btn > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .hv-roles-filter-pop {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 25;
    max-height: 148px;
    overflow: auto;
    padding: 4px;
    border-radius: 10px;
    border: 1px solid var(--border-default);
    background: var(--bg-surface);
    box-shadow: var(--shadow-popover);
  }
  .hv-roles-filter-pop button {
    display: block;
    width: 100%;
    padding: 6px 8px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: var(--text-primary);
    font-size: 12px;
    font-weight: 600;
    text-align: left;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .hv-roles-filter-pop button.is-active,
  .hv-roles-filter-pop button:hover {
    background: var(--bg-subtle);
    color: var(--teal);
  }
  .hv-roles-count {
    display: inline-flex;
    min-width: 18px;
    height: 18px;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
    border-radius: 999px;
    background: var(--teal);
    color: var(--navy);
    font-size: 10px;
    font-weight: 800;
  }
  .hv-roles-columns {
    display: grid;
    gap: 4px;
  }
  .hv-roles-columns label {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 32px;
    padding: 0 8px;
    border-radius: 10px;
    color: var(--text-primary);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }
  .hv-roles-columns label:hover {
    background: var(--bg-subtle);
  }
  .hv-roles-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }
  .hv-roles-chip,
  .hv-roles-icon-btn,
  .hv-roles-select {
    border: 1px solid var(--border-default);
    background: var(--bg-surface);
    color: var(--text-primary);
  }
  .hv-roles-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 28px;
    padding: 0 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
  }
  .hv-roles-icon-btn {
    display: inline-flex;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    cursor: pointer;
  }
  .hv-roles-icon-btn:disabled {
    opacity: 0.4;
  }
  .hv-roles-select {
    width: 100%;
    max-width: 100%;
    height: 36px;
    padding: 0 28px 0 10px;
    border-radius: 10px;
    font-size: 13px;
    color: var(--text-primary);
    appearance: none;
    background-color: var(--bg-surface);
    background-image: linear-gradient(45deg, transparent 50%, var(--text-muted) 50%),
      linear-gradient(135deg, var(--text-muted) 50%, transparent 50%);
    background-position: calc(100% - 14px) calc(50% - 2px), calc(100% - 9px) calc(50% - 2px);
    background-size: 5px 5px, 5px 5px;
    background-repeat: no-repeat;
  }
  .hv-roles-panel,
  .hv-roles-bulk {
    margin-bottom: 12px;
    padding: 14px;
    border-radius: 16px;
  }
  .hv-roles-panel h3,
  .hv-roles-bulk {
    color: var(--text-primary);
  }
  .hv-roles-panel h3 {
    margin: 0;
    font-size: 13px;
    font-weight: 800;
  }
  .hv-roles-bulk {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background: color-mix(in srgb, var(--teal) 8%, var(--bg-surface));
  }
  .hv-roles-table-wrap {
    display: block;
    flex: 1 1 auto;
    overflow-x: auto;
    border-radius: 16px;
  }
  .hv-roles-table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  }
  .hv-roles-table th,
  .hv-roles-table td {
    padding: 10px 12px;
    text-align: left;
    font-size: 13px;
    line-height: 1.25;
    vertical-align: middle;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    border-bottom: 1px solid var(--border-subtle);
    color: var(--text-primary);
  }
  .hv-roles-table th {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--text-muted);
    background: var(--bg-subtle);
  }
  .hv-roles-table .hv-roles-col-check {
    width: 42px;
  }
  .hv-roles-table .hv-roles-col-status {
    width: 100px;
    padding-left: 8px;
    padding-right: 8px;
    overflow: visible;
  }
  .hv-roles-table .hv-roles-col-num {
    width: 88px;
  }
  .hv-roles-table .hv-roles-col-actions {
    width: 96px;
    overflow: visible;
    text-align: right;
  }
  .hv-roles-cell {
    display: flex;
    min-width: 0;
    max-width: 100%;
    align-items: center;
    gap: 6px;
  }
  .hv-roles-cell > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .hv-roles-table tr.is-selected td {
    background: color-mix(in srgb, var(--teal) 10%, var(--bg-surface));
  }
  .hv-roles-name {
    display: block;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    border: 0;
    background: transparent;
    font-weight: 700;
    color: var(--text-primary);
    text-align: left;
    cursor: pointer;
  }
  .hv-roles-status {
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 8px;
    border-radius: 999px;
    border: 1px solid transparent;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.02em;
    white-space: nowrap;
  }
  .hv-roles-status.is-active {
    color: var(--success);
    background: color-mix(in srgb, var(--success) 16%, transparent);
    border-color: color-mix(in srgb, var(--success) 38%, transparent);
  }
  .hv-roles-status.is-inactive {
    color: var(--text-primary);
    background: color-mix(in srgb, var(--text-muted) 18%, transparent);
    border-color: var(--border-default);
  }
  button.hv-roles-status-select {
    display: inline-flex !important;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;
    width: fit-content !important;
    min-width: 0 !important;
    max-width: 84px;
    height: 22px !important;
    min-height: 22px !important;
    padding: 0 4px 0 7px !important;
    border-radius: 999px !important;
    font-size: 11px !important;
    font-weight: 800 !important;
    line-height: 1 !important;
    letter-spacing: 0.01em;
    gap: 2px;
    box-shadow: none !important;
    outline: none !important;
  }
  button.hv-roles-status-select:focus,
  button.hv-roles-status-select:focus-visible {
    box-shadow: none !important;
    outline: none !important;
  }
  button.hv-roles-status-select > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1 !important;
  }
  button.hv-roles-status-select svg {
    width: 12px !important;
    height: 12px !important;
    flex-shrink: 0;
    opacity: 0.9 !important;
  }
  button.hv-roles-status-select.is-active,
  button.hv-roles-status-select.is-active > span,
  button.hv-roles-status-select.is-active svg {
    color: var(--success) !important;
  }
  button.hv-roles-status-select.is-active {
    background: color-mix(in srgb, var(--success) 18%, transparent) !important;
    border-color: color-mix(in srgb, var(--success) 45%, transparent) !important;
  }
  button.hv-roles-status-select.is-inactive,
  button.hv-roles-status-select.is-inactive > span,
  button.hv-roles-status-select.is-inactive svg {
    color: var(--text-secondary) !important;
  }
  button.hv-roles-status-select.is-inactive {
    background: color-mix(in srgb, var(--text-muted) 16%, transparent) !important;
    border-color: var(--border-default) !important;
  }
  button.hv-roles-status-select.is-hold,
  button.hv-roles-status-select.is-hold > span,
  button.hv-roles-status-select.is-hold svg {
    color: var(--warning) !important;
  }
  button.hv-roles-status-select.is-hold {
    background: color-mix(in srgb, var(--warning) 18%, transparent) !important;
    border-color: color-mix(in srgb, var(--warning) 45%, transparent) !important;
  }
  button.hv-roles-status-select.is-closed,
  button.hv-roles-status-select.is-closed > span,
  button.hv-roles-status-select.is-closed svg {
    color: var(--error) !important;
  }
  button.hv-roles-status-select.is-closed {
    background: color-mix(in srgb, var(--error) 16%, transparent) !important;
    border-color: color-mix(in srgb, var(--error) 42%, transparent) !important;
  }
  button.hv-roles-status-select.is-placed,
  button.hv-roles-status-select.is-placed > span,
  button.hv-roles-status-select.is-placed svg {
    color: var(--info) !important;
  }
  button.hv-roles-status-select.is-placed {
    background: color-mix(in srgb, var(--info) 16%, transparent) !important;
    border-color: color-mix(in srgb, var(--info) 42%, transparent) !important;
  }
  .hv-roles-status-menu {
    z-index: 2000 !important;
    width: 156px !important;
    min-width: 156px !important;
    max-width: 156px !important;
    overflow: hidden !important;
    background: var(--bg-surface) !important;
    border: 1px solid var(--border-default) !important;
    border-radius: 10px !important;
    box-shadow: var(--shadow-lg) !important;
  }
  .hv-roles-status-menu [data-radix-select-viewport] {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }
  .hv-roles-status-option,
  .hv-roles-status-option span {
    overflow: visible !important;
    white-space: nowrap !important;
    text-overflow: clip !important;
  }
  .hv-roles-status-option.is-active { color: var(--success) !important; }
  .hv-roles-status-option.is-inactive { color: var(--text-secondary) !important; }
  .hv-roles-status-option.is-hold { color: var(--warning) !important; }
  .hv-roles-status-option.is-closed { color: var(--error) !important; }
  .hv-roles-status-option.is-placed { color: var(--info) !important; }
  .hv-roles-switch {
    position: relative;
    width: 40px;
    height: 22px;
    flex-shrink: 0;
    border: 1px solid color-mix(in srgb, var(--text-muted) 35%, var(--border-default));
    border-radius: 999px;
    background: color-mix(in srgb, var(--text-muted) 28%, var(--bg-subtle));
    cursor: pointer;
  }
  .hv-roles-switch span {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #f4f7fb;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    transition: transform var(--transition-fast);
  }
  .hv-roles-switch.is-on {
    background: var(--teal);
    border-color: var(--teal);
  }
  .hv-roles-switch.is-on span {
    transform: translateX(18px);
    background: #ffffff;
  }
  .hv-roles-cards {
    display: grid;
    gap: 10px;
  }
  .hv-roles-card {
    display: grid;
    gap: 4px;
    padding: 12px;
    border-radius: 16px;
  }
  .hv-roles-card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  .hv-roles-card-tools {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }
  .hv-roles-card-meta {
    margin: 0 0 0 28px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 11px;
    color: var(--text-muted);
  }
  .hv-roles-card-facts {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3px 8px;
    margin: 0 0 0 26px;
  }
  .hv-roles-card-facts span {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 4px;
    color: var(--text-muted);
  }
  .hv-roles-card-facts em {
    font-size: 9px;
    font-style: normal;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    flex-shrink: 0;
  }
  .hv-roles-card-facts strong {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 11px;
    font-weight: 650;
    color: var(--text-primary);
  }
  .hv-roles-card-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 12px;
    margin: 0;
  }
  .hv-roles-card-grid div {
    min-width: 0;
  }
  .hv-roles-card-grid dt {
    margin: 0 0 2px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--text-muted);
  }
  .hv-roles-card-grid dd {
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
  }
  .hv-roles-card-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
  .hv-roles-card.is-selected {
    border-color: color-mix(in srgb, var(--teal) 45%, var(--border-default));
    background: color-mix(in srgb, var(--teal) 8%, var(--bg-surface));
  }
  .hv-roles-empty {
    display: grid;
    place-items: center;
    gap: 8px;
    padding: 48px 20px;
    border-radius: 16px;
    text-align: center;
    color: var(--text-muted);
  }
  .hv-roles-empty h3 {
    margin: 0;
    color: var(--text-primary);
  }
  .hv-roles-pager {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 12px;
    color: var(--text-muted);
    font-size: 12px;
  }
  .hv-roles-drawer {
    position: fixed;
    inset: 4.25rem 0 0 0;
    z-index: 180;
  }
  .hv-roles-drawer-mask {
    position: absolute;
    inset: 0;
    border: 0;
    background: color-mix(in srgb, black 45%, transparent);
  }
  .hv-roles-drawer-panel {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    width: min(320px, 88vw);
    overflow: auto;
    padding: 14px;
    background: var(--bg-page);
    box-shadow: var(--shadow-popover);
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .hv-roles-drawer-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
  }
  .hv-roles-drawer-head h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 800;
    color: var(--text-primary);
  }
  .hv-roles-drawer-close {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 10px;
    border: 1px solid var(--border-default);
    border-radius: 10px;
    background: var(--bg-surface);
    color: var(--text-primary);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
  }
  .hv-roles-modal .MuiDialog-paper {
    overflow: hidden;
    border: 1px solid color-mix(in srgb, var(--teal) 24%, var(--border-default));
    border-radius: 20px;
    background: var(--bg-surface);
    box-shadow: var(--shadow-popover);
  }
  .hv-roles-modal .MuiDialogTitle-root {
    padding: 14px 18px;
    border-bottom: 1px solid var(--border-default);
    background: var(--bg-surface);
  }
  .hv-roles-modal .MuiDialogContent-root {
    padding: 12px 14px 8px;
    background: var(--bg-page);
  }
  .hv-roles-modal .MuiDialogActions-root {
    padding: 12px 16px;
    border-top: 1px solid var(--border-default);
    background: var(--bg-surface);
  }
  .hv-role-form-brand {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .hv-role-form-brand-mark {
    display: inline-flex;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    color: var(--teal);
    background: color-mix(in srgb, var(--teal) 14%, transparent);
    border: 1px solid color-mix(in srgb, var(--teal) 28%, transparent);
  }
  .hv-role-form-brand h2 {
    margin: 0;
    font-size: 17px;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: var(--text-primary);
  }
  .hv-role-form-brand p {
    margin: 1px 0 0;
    font-size: 12px;
    color: var(--text-muted);
  }
  .hv-role-form-close {
    display: inline-flex;
    width: 34px;
    height: 34px;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--border-default);
    border-radius: 10px;
    background: var(--bg-surface);
    color: var(--text-secondary);
    cursor: pointer;
  }
  .hv-role-form-close:hover {
    color: var(--text-primary);
    background: var(--bg-subtle);
  }
  .hv-role-form-tabs {
    display: grid;
    gap: 6px;
  }
  .hv-role-form-tab {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 10px;
    padding: 9px 10px;
    border: 1px solid var(--border-default);
    border-radius: 12px;
    background: var(--bg-surface);
    color: var(--text-primary);
    text-align: left;
    cursor: pointer;
  }
  .hv-role-form-tab:hover {
    border-color: color-mix(in srgb, var(--teal) 35%, var(--border-default));
    background: color-mix(in srgb, var(--teal) 8%, var(--bg-surface));
  }
  .hv-role-form-tab.is-active {
    border-color: color-mix(in srgb, var(--teal) 45%, var(--border-default));
    background: color-mix(in srgb, var(--teal) 12%, var(--bg-surface));
  }
  .hv-role-form-tab-icon {
    display: flex;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    color: var(--teal);
    background: color-mix(in srgb, var(--teal) 14%, transparent);
  }
  .hv-role-form-tab-icon svg {
    display: block;
    width: 15px;
    height: 15px;
    margin: 0;
  }
  .hv-role-form-tab-copy {
    min-width: 0;
    display: block;
  }
  .hv-role-form-tab-copy strong {
    display: block;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.2;
    color: var(--text-primary);
  }
  .hv-role-form-tab-copy small {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    line-height: 1.3;
    color: var(--text-muted);
  }
  .hv-role-form-mobile-nav {
    display: none;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 10px;
    padding: 8px 10px;
    border: 1px solid var(--border-default);
    border-radius: 12px;
    background: var(--bg-surface);
  }
  .hv-role-form-mobile-nav button {
    display: inline-flex;
    width: 32px;
    height: 32px;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--border-default);
    border-radius: 10px;
    background: var(--bg-subtle);
    color: var(--text-primary);
    cursor: pointer;
  }
  .hv-role-form-mobile-nav h3 {
    margin: 0;
    font-size: 13px;
    font-weight: 800;
    color: var(--text-primary);
  }
  .hv-role-form-mobile-nav p {
    margin: 2px 0 0;
    font-size: 11px;
    color: var(--text-muted);
  }
  .hv-role-form-footer-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 14px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }
  .hv-role-form-footer-btn.is-ghost {
    border: 1px solid var(--border-default);
    background: var(--bg-surface);
    color: var(--text-secondary);
  }
  .hv-role-form-footer-btn.is-primary {
    border: 0;
    background: var(--teal);
    color: var(--navy);
  }
  .hv-role-form-footer-btn:disabled {
    opacity: 0.4;
    cursor: default;
  }
  .hv-roles-modal .MuiCard-root {
    margin-bottom: 12px;
    border: 1px solid color-mix(in srgb, var(--teal) 16%, var(--border-default));
    border-radius: 16px;
    background: var(--bg-surface);
    box-shadow: var(--shadow-sm);
    transform: none !important;
  }
  .hv-roles-modal .MuiCardContent-root {
    padding: 16px !important;
  }
  .hv-roles-modal .MuiOutlinedInput-root {
    min-height: 42px;
    border-radius: 12px;
    background: var(--bg-surface);
  }
  .hv-roles-modal .MuiOutlinedInput-notchedOutline {
    border-color: var(--border-default);
  }
  .hv-roles-modal .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: color-mix(in srgb, var(--teal) 40%, var(--border-default));
  }
  .hv-roles-modal .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-width: 1px;
    border-color: var(--teal);
  }
  .hv-roles-modal .MuiInputLabel-root,
  .hv-roles-modal .MuiFormLabel-root {
    color: var(--text-muted);
  }
  .hv-roles-modal .MuiInputLabel-root.Mui-focused {
    color: var(--teal);
  }
  .hv-roles-modal .MuiChip-root {
    max-width: 100%;
    border-radius: 999px;
    background: var(--bg-subtle);
    color: var(--text-primary);
  }
  .hv-roles-modal .MuiChip-label {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .hv-roles-modal .MuiButton-contained {
    border-radius: 10px;
    background: var(--teal);
    color: var(--navy);
    box-shadow: none;
    text-transform: none;
    font-weight: 700;
  }
  .hv-roles-modal .MuiButton-contained:hover {
    background: color-mix(in srgb, var(--teal) 86%, black);
    box-shadow: none;
  }
  .hv-roles-modal .MuiButton-outlined {
    border-radius: 10px;
    border-color: var(--border-default);
    color: var(--text-primary);
    text-transform: none;
    font-weight: 700;
  }
  .hv-roles-modal .MuiTypography-h5,
  .hv-roles-modal .MuiTypography-h6 {
    color: var(--text-primary);
  }
  @media (max-width: 768px) {
    .hv-roles-page {
      padding: 10px 0 24px;
    }
    .hv-roles-shell {
      grid-template-columns: minmax(0, 1fr);
    }
    .hv-roles-aside {
      display: none !important;
    }
    .hv-roles-main {
      padding: 12px;
      border-radius: 16px;
    }
    .hv-roles-table-wrap {
      display: none !important;
    }
    .hv-roles-cards {
      display: grid;
      gap: 6px;
    }
    .hv-roles-card {
      gap: 2px;
      padding: 7px 8px;
      border-radius: 10px;
    }
    .hv-roles-card-meta {
      margin-left: 26px;
      font-size: 10px;
    }
    .hv-roles-name {
      font-size: 13px;
    }
    .hv-roles-icon-btn {
      width: 28px;
      height: 28px;
    }
    .hv-roles-switch {
      width: 36px;
      height: 20px;
    }
    .hv-roles-switch span {
      width: 14px;
      height: 14px;
    }
    .hv-roles-switch.is-on span {
      transform: translateX(16px);
    }
    .hv-roles-status {
      height: 18px;
      padding: 0 6px;
      font-size: 10px;
    }
    .hv-roles-filter-section {
      grid-template-columns: 1fr;
      padding: 8px;
      overflow: visible;
    }
    .hv-roles-filter-pop {
      max-height: 132px;
    }
    .hv-roles-card-facts {
      margin-left: 24px;
    }
    .hv-roles-menu-panel,
    .hv-roles-menu-panel.is-end {
      left: auto;
      right: 0;
      width: min(200px, calc(100vw - 20px));
      max-height: 220px;
    }
    .hv-roles-rail-actions {
      grid-template-columns: 1fr;
    }
    .hv-roles-modal .MuiDialog-paper {
      display: flex;
      flex-direction: column;
      width: 100% !important;
      max-width: 100% !important;
      height: 100dvh;
      max-height: 100dvh;
      margin: 0;
      border-radius: 0;
    }
    .hv-roles-modal .MuiDialogTitle-root {
      flex-shrink: 0;
      padding: 10px 12px;
      gap: 8px;
    }
    .hv-role-form-brand {
      min-width: 0;
    }
    .hv-role-form-brand h2 {
      font-size: 15px;
    }
    .hv-role-form-brand p {
      font-size: 11px;
    }
    .hv-roles-modal .MuiDialogContent-root {
      flex: 1 1 auto;
      min-height: 0;
      height: auto !important;
      overflow: auto;
      padding: 8px 10px 12px;
    }
    .hv-roles-modal .hv-role-form-pane {
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
      padding-left: 0 !important;
    }
    .hv-roles-modal .MuiDialogActions-root {
      flex-shrink: 0;
      padding: 10px 12px calc(10px + env(safe-area-inset-bottom));
    }
    .hv-roles-modal .MuiDialogActions-root .hv-role-form-footer-btn {
      flex: 1 1 auto;
      justify-content: center;
      min-width: 0;
    }
    .hv-role-form-mobile-nav {
      display: flex;
    }
    .hv-roles-modal .MuiCardContent-root {
      padding: 12px !important;
    }
    .hv-roles-modal .MuiPagination-ul {
      flex-wrap: wrap;
      justify-content: center;
    }
    .hv-roles-modal .MuiPaginationItem-firstLast {
      display: none;
    }
  }

  .hv-tl-item { align-items: stretch; }
  .hv-profile .hv-tl-item {
    margin-bottom: 10px;
    padding: 0;
  }
  .hv-profile .hv-tl-item:last-child { margin-bottom: 0; }
  .hv-tl-rail {
    position: relative;
    width: 22px;
    flex-shrink: 0;
  }
  .hv-tl-rail::before {
    content: "";
    position: absolute;
    top: 22px;
    bottom: -10px;
    left: 10px;
    width: 1px;
    background: color-mix(in srgb, var(--teal) 40%, var(--border-default));
  }
  .hv-tl-item:last-child .hv-tl-rail::before { display: none; }
  .hv-tl-dot {
    position: relative;
    z-index: 1;
    width: 8px;
    height: 8px;
    margin-top: 10px;
    margin-left: 7px;
    border-radius: 50%;
    border: 1px solid var(--bg-surface);
    background: var(--teal);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--teal) 22%, transparent);
  }
  .hv-tl-dot--edu { background: var(--info); box-shadow: 0 0 0 3px color-mix(in srgb, var(--info) 22%, transparent); }
  .hv-tl-mark {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    margin-top: 6px;
    border-radius: 50%;
    border: 1px solid color-mix(in srgb, var(--teal) 40%, var(--border-default));
    background: color-mix(in srgb, var(--teal) 18%, var(--bg-surface));
    color: var(--teal);
    font-size: 11px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: 0;
  }
  .hv-tl-mark--exp {
    background: color-mix(in srgb, var(--teal) 18%, var(--bg-surface));
    color: var(--teal);
  }
  .hv-tl-mark--edu {
    background: color-mix(in srgb, var(--info) 16%, var(--bg-surface));
    border-color: color-mix(in srgb, var(--info) 40%, var(--border-default));
    color: var(--info);
  }
  .hv-tl-card,
  .hv-tl-body {
    background: var(--bg-surface);
    border: 1px solid color-mix(in srgb, var(--border-default) 88%, transparent);
    border-radius: 12px;
    padding: 10px 10px 10px 12px;
  }
  .hv-tl-item:last-child .hv-tl-card,
  .hv-tl-item:last-child .hv-tl-body {
    border-bottom: 1px solid color-mix(in srgb, var(--border-default) 88%, transparent);
  }
  .hv-tl-card--exp,
  .hv-tl-card--edu { border-left: 0; }
  .hv-date-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 10rem;
    padding: 2px 8px;
    border-radius: 999px;
    background: color-mix(in srgb, var(--teal) 12%, var(--bg-subtle));
    color: var(--text-secondary);
    font-size: 10.5px;
    font-weight: 700;
    white-space: nowrap;
  }
  .hv-date-pill--edu {
    background: color-mix(in srgb, var(--info) 12%, var(--bg-subtle));
  }
  .hv-desc {
    padding: 0;
    border-radius: 0;
    background: transparent;
    color: var(--text-secondary);
    line-height: 1.4;
  }

  .cm-edit-form {
    min-width: 0;
  }
  .cm-edit-form .MuiOutlinedInput-root {
    min-width: 0;
  }
  .cm-stat {
    padding: 0.15rem 0.2rem 0.15rem 0;
  }

  .hv-file-card,
  .hv-mail-card {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 8px 10px;
    border: 1px solid color-mix(in srgb, var(--info) 22%, var(--border-default));
    border-left: 3px solid var(--info);
    border-radius: 10px;
    background: color-mix(in srgb, var(--info) 7%, var(--bg-surface));
    cursor: pointer;
  }
  .hv-mail-card {
    align-items: center;
  }
  .hv-mail-wrap {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .hv-mail-wrap.is-open .hv-mail-card {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    background: color-mix(in srgb, var(--info) 12%, var(--bg-surface));
  }
  .hv-mail-detail {
    position: relative;
    min-width: 0;
    padding: 10px 12px 12px;
    overflow: auto;
    border: 1px solid color-mix(in srgb, var(--info) 22%, var(--border-default));
    border-top: 0;
    border-radius: 0 0 10px 10px;
    background: var(--bg-surface);
  }
  .hv-mail-close {
    position: absolute !important;
    top: 4px;
    right: 4px;
    z-index: 2;
    color: var(--text-secondary) !important;
  }
  .hv-mail-close:hover {
    color: var(--text-primary) !important;
    background: color-mix(in srgb, var(--info) 12%, transparent) !important;
  }
  .hv-mail-body {
    max-width: 100%;
    overflow-x: auto;
    word-break: break-word;
  }
  .hv-mail-body img,
  .hv-mail-body table {
    max-width: 100% !important;
    height: auto !important;
  }
  .hv-file-card {
    cursor: default;
    border-color: color-mix(in srgb, var(--teal) 22%, var(--border-default));
    border-left-color: var(--teal);
    background: color-mix(in srgb, var(--teal) 7%, var(--bg-surface));
  }
  .hv-mail-card:hover,
  .hv-file-card:hover {
    background: color-mix(in srgb, var(--teal) 12%, var(--bg-surface));
  }
  .hv-mail-card:hover {
    background: color-mix(in srgb, var(--info) 12%, var(--bg-surface));
  }
  .hv-file-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: color-mix(in srgb, var(--info) 16%, var(--bg-subtle));
    color: var(--info);
    flex-shrink: 0;
  }
  .hv-file-card .hv-file-icon {
    background: color-mix(in srgb, var(--teal) 16%, var(--bg-subtle));
    color: var(--teal);
  }

  .hv-log-rail { display: flex; flex-direction: column; gap: 8px; }
  .hv-log-item {
    display: flex;
    gap: 8px;
    position: relative;
  }
  .hv-log-dot {
    width: 8px;
    height: 8px;
    margin-top: 12px;
    flex-shrink: 0;
    border-radius: 50%;
    background: var(--teal);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--teal) 18%, transparent);
  }
  .hv-log-card {
    flex: 1;
    min-width: 0;
    padding: 8px 10px;
    border-radius: 10px;
    background: color-mix(in srgb, var(--teal) 7%, var(--bg-surface));
    border: 1px solid color-mix(in srgb, var(--teal) 22%, var(--border-default));
    border-left: 3px solid var(--teal);
  }
  .hv-log-card.is-editing {
    background: color-mix(in srgb, var(--teal) 10%, var(--bg-surface));
  }
  .hv-remark {
    padding: 4px 8px;
    border-radius: 6px;
    background: color-mix(in srgb, var(--bg-subtle) 70%, transparent);
    border-left: 2px solid var(--teal);
  }
  .hv-remark-editor {
    position: relative;
    margin-top: 10px;
    padding: 10px;
    border: 1px solid color-mix(in srgb, var(--teal) 24%, var(--border-default));
    border-radius: 10px;
    background: var(--bg-surface);
  }
  .hv-remark-title {
    padding-right: 32px;
  }
  .hv-remark-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 10px;
    position: relative;
    z-index: 1;
  }
  .hv-remark-close {
    position: absolute !important;
    top: 4px;
    right: 4px;
    z-index: 2;
    color: var(--text-secondary) !important;
  }
  .hv-remark-close:hover {
    color: var(--text-primary) !important;
    background: color-mix(in srgb, var(--teal) 12%, transparent) !important;
  }

  /* ExecEdge sales funnel */
  :root {
    --ee-gold: #fbbf24;
    --ee-gold-light: #fde68a;
    --ee-gold-dark: #d97706;
    --ee-gold-accent: #fb923c;
    --ee-green: #4ade80;
    --ee-green-light: #6ee7b7;
    --ee-blue: #62B5A3;
    --ee-pink: #f472b6;
    --ee-red: #f87171;
    --ee-red-strong: #dc2626;
    --ee-slate-600: #2A3553;
    --ee-slate-700: #1F3A78;
    --ee-ink: #0A1838;
    --ee-white: #ffffff;
    --ee-gold-rgb: 251, 191, 36;
    --ee-green-rgb: 52, 211, 153;
    --ee-blue-rgb: 96, 165, 250;
    --ee-red-rgb: 239, 68, 68;
    --ee-white-rgb: 255, 255, 255;
    --ee-text: var(--text-inverse);
    --ee-text-sub: color-mix(in srgb, var(--text-inverse) 70%, transparent);
    --ee-text-muted: color-mix(in srgb, var(--text-inverse) 50%, transparent);
    --ee-text-faint: color-mix(in srgb, var(--text-inverse) 35%, transparent);
    --ee-text-ghost: color-mix(in srgb, var(--text-inverse) 25%, transparent);
    --ee-bg-root: radial-gradient(ellipse at top, hsl(220, 40%, 13%) 0%, hsl(220, 35%, 8%) 60%);
    --ee-bg-card: rgba(255, 255, 255, 0.04);
    --ee-bg-card-hover: rgba(255, 255, 255, 0.07);
    --ee-bg-input: rgba(255, 255, 255, 0.06);
    --ee-bg-dropdown: hsl(220, 35%, 11%);
    --ee-bg-muted: rgba(255, 255, 255, 0.03);
    --ee-border: rgba(255, 255, 255, 0.08);
    --ee-border-gold: rgba(251, 191, 36, 0.2);
    --ee-border-green: rgba(52, 211, 153, 0.2);
  }

  .ee-root {
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
    overflow-y: auto;
    color: var(--ee-text);
    background: var(--ee-bg-root);
    font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .ee-blob {
    position: fixed;
    pointer-events: none;
    border-radius: 50%;
    z-index: 0;
  }

  .ee-blob--top {
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 800px;
    height: 400px;
    background: rgba(234, 179, 8, 0.05);
    filter: blur(120px);
  }

  .ee-blob--bottom {
    right: 0;
    bottom: 0;
    width: 420px;
    height: 420px;
    background: rgba(16, 185, 129, 0.05);
    filter: blur(100px);
  }

  .ee-progress-track {
    position: fixed;
    inset: 0 0 auto 0;
    z-index: 50;
    height: 3px;
    overflow: hidden;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.1);
  }

  .ee-progress-fill {
    height: 100%;
    transform-origin: left center;
    background: linear-gradient(90deg, var(--ee-gold), var(--ee-gold-light));
  }

  .ee-logo-float {
    position: fixed;
    top: 16px;
    left: 24px;
    z-index: 40;
    font-size: 20px;
    font-weight: 900;
    letter-spacing: -0.02em;
  }

  .ee-screen {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
    position: relative;
    z-index: 1;
  }

  .ee-container {
    width: 100%;
    max-width: 672px;
  }

  .ee-container--wide {
    max-width: 820px;
  }

  .ee-container--narrow {
    max-width: 560px;
  }

  .ee-gradient-text-gold {
    background: linear-gradient(135deg, var(--ee-gold) 0%, var(--ee-gold-light) 50%, var(--ee-gold-dark) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
  }

  .ee-gradient-text-green {
    background: linear-gradient(135deg, var(--ee-green) 0%, var(--ee-green-light) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
  }

  .ee-title-display,
  .ee-title-h1,
  .ee-title-h2 {
    margin: 0;
    line-height: 1.1;
    letter-spacing: -0.02em;
    font-weight: 900;
    color: var(--ee-text);
  }

  .ee-title-display {
    font-size: clamp(42px, 6vw, 64px);
  }

  .ee-title-h1 {
    font-size: clamp(26px, 3.5vw, 42px);
  }

  .ee-title-h2 {
    font-size: clamp(20px, 3vw, 34px);
    line-height: 1.2;
  }

  .ee-divider {
    height: 1px;
    margin: 18px 0;
    background: linear-gradient(90deg, transparent, var(--ee-border-gold), transparent);
  }

  .ee-divider--center {
    max-width: 400px;
    margin: 28px auto;
  }

  .ee-card {
    padding: 24px;
    border-radius: 16px;
    border: 1px solid var(--ee-border);
    background: var(--ee-bg-card);
  }

  .ee-card--gold-border {
    border-color: var(--ee-border-gold);
  }

  .ee-card--tight {
    padding: 12px 16px;
  }

  .ee-card--compact {
    max-width: 520px;
    margin: 0 auto;
  }

  .ee-card--centered {
    margin-left: auto;
    margin-right: auto;
  }

  .ee-text-lead {
    margin: 0;
    font-size: clamp(18px, 2.2vw, 24px);
    line-height: 1.35;
    font-weight: 700;
    color: var(--ee-text);
  }

  .ee-text-body-lg {
    margin: 0;
    font-size: 16px;
    line-height: 1.6;
    color: var(--ee-text-sub);
  }

  .ee-text-body {
    margin: 0;
    font-size: 15px;
    line-height: 1.6;
    color: var(--ee-text-muted);
  }

  .ee-text-body-sm {
    margin: 0;
    font-size: 13px;
    line-height: 1.55;
    color: var(--ee-text-sub);
  }

  .ee-text-caption {
    margin: 0;
    font-size: 14px;
    color: var(--ee-text-muted);
  }

  .ee-text-strong {
    color: var(--ee-text-sub);
    font-weight: 700;
  }

  .ee-text-main {
    color: var(--ee-text);
  }

  .ee-text-gold {
    color: var(--ee-gold);
  }

  .ee-text-gold-strong {
    color: var(--ee-gold);
    font-weight: 700;
  }

  .ee-text-faint {
    margin: 0;
    color: rgba(255, 255, 255, 0.12);
    font-size: 11px;
  }

  .ee-text-center {
    text-align: center;
  }

  .ee-text-left {
    text-align: left;
  }

  .ee-text-micro {
    margin: 10px 0 0 4px;
    font-size: 11px;
    color: var(--ee-text-ghost);
  }

  .ee-em {
    color: var(--ee-text);
    font-style: normal;
    font-weight: 500;
  }

  .ee-overline {
    margin: 0;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-weight: 700;
    color: var(--ee-text-faint);
  }

  .ee-quoted-line {
    margin: 8px 0 0;
    font-style: italic;
    font-size: 14px;
    line-height: 1.6;
    color: var(--ee-gold-light);
  }

  .ee-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .ee-badge--gold {
    color: var(--ee-gold);
    background: rgba(251, 191, 36, 0.1);
    border: 1px solid rgba(251, 191, 36, 0.2);
  }

  .ee-badge--green {
    color: var(--ee-green);
    background: rgba(52, 211, 153, 0.1);
    border: 1px solid rgba(52, 211, 153, 0.2);
  }

  .ee-pulse-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
  }

  .ee-tone-gold {
    color: var(--ee-gold);
    background-color: var(--ee-gold);
  }

  .ee-tone-green {
    color: var(--ee-green);
    background-color: var(--ee-green);
  }

  .ee-tone-blue {
    color: var(--ee-blue);
    background-color: var(--ee-blue);
  }

  .ee-tone-pink {
    color: var(--ee-pink);
    background-color: var(--ee-pink);
  }

  .ee-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    border: 0;
    border-radius: 16px;
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.01em;
    text-decoration: none;
    padding: 16px 32px;
    cursor: pointer;
    transition: opacity 0.2s ease;
  }

  .ee-btn--gold {
    color: #1a1a2e;
    background: linear-gradient(135deg, var(--ee-gold), var(--ee-gold-dark));
    box-shadow: 0 0 24px rgba(251, 191, 36, 0.2);
  }

  .ee-btn.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .ee-btn--full {
    width: 100%;
  }

  .ee-btn--wide {
    min-width: 240px;
  }

  .ee-btn--link {
    text-decoration: none;
  }

  .ee-back-btn {
    border: 0;
    background: transparent;
    padding: 0;
    margin: 0 0 40px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--ee-text-muted);
    font-size: 14px;
    cursor: pointer;
    transition: color 0.2s ease;
  }

  .ee-back-btn:hover {
    color: var(--ee-text-sub);
  }

  .ee-chip-row {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .ee-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 5px 12px;
    border-radius: 999px;
    border: 1px solid var(--ee-border);
    background: rgba(255, 255, 255, 0.05);
    color: var(--ee-text-muted);
    font-size: 12px;
    font-weight: 500;
  }

  .ee-chip-icon {
    display: flex;
    color: var(--ee-green);
  }

  .ee-footer-note {
    margin-top: 28px;
    padding-top: 20px;
    border-top: 1px solid var(--ee-border);
  }

  .ee-stat-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  .ee-stat-box {
    text-align: center;
    border-radius: 10px;
    padding: 10px 16px;
    border: 1px solid var(--ee-border-gold);
    background: rgba(251, 191, 36, 0.1);
  }

  .ee-stat-num {
    font-size: 24px;
    font-weight: 900;
  }

  .ee-stat-label {
    margin-top: 2px;
    font-size: 11px;
    color: var(--ee-text-muted);
  }

  .ee-founders-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
    align-items: stretch;
  }

  .ee-founder-card {
    border-radius: 16px;
    padding: 12px;
    min-height: 210px;
    border: 1px solid var(--ee-border);
    background: var(--ee-bg-card);
  }

  .ee-founder-avatar {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: 900;
    margin-bottom: 10px;
  }

  .ee-founder-photo {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .ee-founder-avatar.ee-tone-gold {
    border: 2px solid rgba(251, 191, 36, 0.25);
    background: rgba(251, 191, 36, 0.1);
  }

  .ee-founder-avatar.ee-tone-green {
    border: 2px solid rgba(52, 211, 153, 0.25);
    background: rgba(52, 211, 153, 0.1);
  }

  .ee-founder-avatar.ee-tone-blue {
    border: 2px solid rgba(96, 165, 250, 0.25);
    background: rgba(96, 165, 250, 0.1);
  }

  .ee-founder-avatar.ee-tone-pink {
    border: 2px solid rgba(244, 114, 182, 0.25);
    background: rgba(244, 114, 182, 0.1);
  }

  .ee-founder-name {
    margin-bottom: 4px;
    font-size: 16px;
    font-weight: 700;
  }

  .ee-founder-name--gold {
    color: var(--ee-gold);
  }

  .ee-founder-name--green {
    color: var(--ee-green);
  }

  .ee-founder-name--blue {
    color: var(--ee-blue);
  }

  .ee-founder-name--pink {
    color: var(--ee-pink);
  }

  .ee-founder-inst {
    margin-bottom: 6px;
    font-size: 11px;
    color: var(--ee-text-muted);
  }

  .ee-founder-tag {
    display: inline-block;
    margin-bottom: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    color: var(--ee-gold-light);
    background: rgba(251, 191, 36, 0.1);
  }

  .ee-founder-companies {
    margin: 0;
    font-size: 11px;
    line-height: 1.4;
    color: var(--ee-text-faint);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .ee-options-wrap,
  .ee-form-stack,
  .ee-breakpoints-wrap,
  .ee-points-stack {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .ee-trait-wrap {
    display: flex;
    flex-direction: row;
    gap: 16px;
    align-items: stretch;
  }

  .ee-feature-stack {
    display: flex;
    flex-direction: row;
    gap: 16px;
    align-items: stretch;
  }

  .ee-option-card {
    width: 100%;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 14px;
    border-radius: 12px;
    padding: 18px 20px;
    border: 1.5px solid var(--ee-border);
    background: var(--ee-bg-muted);
    color: var(--ee-text-sub);
    cursor: pointer;
    font-size: 14px;
    line-height: 1.5;
    transition: border-color 0.25s ease, background-color 0.25s ease;
  }

  .ee-option-card:hover {
    border-color: rgba(251, 191, 36, 0.38);
    background: rgba(251, 191, 36, 0.04);
  }

  .ee-option-card.is-selected {
    border-color: var(--ee-gold);
    background: rgba(251, 191, 36, 0.1);
  }

  .ee-option-radio {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    flex-shrink: 0;
    border: 2px solid var(--ee-text-muted);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ee-option-card.is-selected .ee-option-radio {
    border-color: var(--ee-gold);
    background: var(--ee-gold);
  }

  .ee-option-radio-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #1a1a2e;
  }

  .ee-analysis-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }

  .ee-analysis-icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    border: 1px solid rgba(251, 191, 36, 0.25);
    background: rgba(251, 191, 36, 0.12);
    color: var(--ee-gold);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ee-breakpoint-item {
    display: flex;
    gap: 16px;
    border-left: 3px solid var(--ee-gold);
    border-radius: 0 10px 10px 0;
    padding: 18px;
    background: var(--ee-bg-muted);
  }

  .ee-breakpoint-index {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(251, 191, 36, 0.25);
    background: rgba(251, 191, 36, 0.12);
    color: var(--ee-gold);
    font-size: 11px;
    font-weight: 700;
  }

  .ee-breakpoint-text {
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: var(--ee-text-sub);
  }

  .ee-success-note {
    border-radius: 12px;
    border: 1px solid rgba(52, 211, 153, 0.25);
    background: rgba(52, 211, 153, 0.08);
    padding: 16px 20px;
  }

  .ee-success-text {
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: rgba(110, 231, 183, 0.85);
  }

  .ee-feature-card,
  .ee-trait-item {
    display: flex;
    flex-direction: column;
    gap: 12px;
    border-radius: 12px;
    padding: 14px;
    border: 1px solid var(--ee-border);
    background: var(--ee-bg-muted);
    transition: all 0.25s ease;
    flex: 1;
    align-items: center;
    text-align: center;
  }

  .ee-feature-card {
    flex-direction: row;
    align-items: flex-start;
    text-align: left;
  }

  .ee-feature-card:hover {
    border-color: var(--ee-border-gold);
    background: var(--ee-bg-card-hover);
    transform: translateX(4px);
  }

  .ee-feature-icon,
  .ee-trait-icon,
  .ee-lock-float {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ee-lock-float {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    margin-bottom: 24px;
    border: 1px solid rgba(251, 191, 36, 0.25);
    background: rgba(251, 191, 36, 0.1);
    color: var(--ee-gold);
  }

  .ee-feature-icon--gold {
    border: 1px solid rgba(251, 191, 36, 0.25);
    background: rgba(251, 191, 36, 0.1);
    color: var(--ee-gold);
  }

  .ee-feature-icon--green {
    border: 1px solid rgba(52, 211, 153, 0.25);
    background: rgba(52, 211, 153, 0.1);
    color: var(--ee-green);
  }

  .ee-feature-icon--blue {
    border: 1px solid rgba(96, 165, 250, 0.25);
    background: rgba(96, 165, 250, 0.1);
    color: var(--ee-blue);
  }

  .ee-feature-title {
    margin-bottom: 4px;
    font-size: 14px;
    font-weight: 700;
    color: var(--ee-text-sub);
  }

  .ee-feature-desc {
    font-size: 13px;
    line-height: 1.6;
    color: var(--ee-text-muted);
    display: none;
  }

  .ee-tag-wrap {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .ee-tag {
    padding: 6px 14px;
    border-radius: 999px;
    border: 1px solid var(--ee-border);
    background: rgba(255, 255, 255, 0.05);
    color: var(--ee-text-muted);
    font-size: 11px;
    font-weight: 500;
  }

  .ee-note-gradient {
    border-radius: 12px;
    padding: 20px 24px;
    border: 1px solid var(--ee-border-gold);
    background: linear-gradient(135deg, rgba(251, 191, 36, 0.08), rgba(52, 211, 153, 0.08));
  }

  .ee-input-field {
    display: grid;
    gap: 6px;
  }

  .ee-input-label {
    display: block;
    margin-bottom: 4px;
    color: var(--ee-text-muted);
    font-size: 14px;
    font-weight: 500;
  }

  .ee-input-optional {
    color: var(--ee-text-faint);
    font-size: 12px;
    font-weight: 400;
  }

  .ee-input-required {
    color: var(--ee-gold);
  }

  .ee-input-sub-label {
    margin: 0 0 8px;
    font-size: 12px;
    color: var(--ee-text-faint);
  }

  .ee-input-wrap {
    position: relative;
  }

  .ee-input-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    color: var(--ee-text-faint);
    pointer-events: none;
  }

  .ee-input {
    width: 100%;
    color: var(--ee-text);
    outline: 0;
    font-size: 15px;
    padding: 14px 14px 14px 44px;
    border-radius: 10px;
    border: 1.5px solid rgba(255, 255, 255, 0.12);
    background: var(--ee-bg-input);
    transition: all 0.2s ease;
  }

  .ee-input::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  .ee-input-wrap:focus-within .ee-input {
    border-color: var(--ee-gold);
    background: rgba(251, 191, 36, 0.04);
    box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.1);
  }

  .ee-input-wrap--error .ee-input {
    border-color: var(--ee-red);
  }

  .ee-input-error {
    margin: 6px 0 0;
    color: var(--ee-red);
    font-size: 12px;
  }

  .ee-select-wrap {
    position: relative;
  }

  .ee-select {
    width: 100%;
    color: var(--ee-text);
    outline: 0;
    font-size: 15px;
    padding: 14px 44px 14px 14px;
    border-radius: 10px;
    border: 1.5px solid rgba(255, 255, 255, 0.12);
    background: var(--ee-bg-input);
    transition: all 0.2s ease;
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
    color-scheme: dark;
  }

  .ee-select.is-placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  .ee-select option {
    background: #1e1e21;
    color: #ffffff;
  }

  .ee-select option:disabled {
    color: rgba(255, 255, 255, 0.4);
  }

  .ee-select:focus {
    border-color: var(--ee-gold);
    background: rgba(251, 191, 36, 0.04);
    box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.1);
  }

  .ee-select-wrap--error .ee-select {
    border-color: var(--ee-red);
  }

  .ee-select-chevron {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--ee-text-faint);
    pointer-events: none;
  }

  .ee-country-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 50;
    margin: 0;
    padding: 4px 0;
    list-style: none;
    max-height: 240px;
    overflow-y: auto;
    border-radius: 10px;
    border: 1.5px solid rgba(255, 255, 255, 0.12);
    background-color: var(--ee-bg-dropdown);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55);
    isolation: isolate;
  }

  .ee-country-option {
    display: block;
    width: 100%;
    padding: 10px 14px 10px 44px;
    border: 0;
    background-color: var(--ee-bg-dropdown);
    color: var(--ee-text);
    font-size: 14px;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .ee-country-option:hover,
  .ee-country-option.is-selected {
    background: rgba(251, 191, 36, 0.12);
    color: var(--ee-gold);
  }

  /* ── Privacy & Consent (admin + user + apply) ── */
  .hv-privacy {
    --hv-priv-navy: var(--navy);
    --hv-priv-teal: var(--teal);
    width: 100%;
    max-width: 1180px;
    margin: 0 auto;
    padding: 28px 20px 48px;
    font-family: var(--font-sans);
    color: var(--text-primary);
  }
  .hv-privacy.hv-privacy-shell {
    max-width: none;
    margin: 0;
    padding: 0;
  }
  .hv-privacy--narrow { max-width: 880px; }
  .hv-privacy--accept { max-width: 760px; padding-bottom: 120px; }

  .hv-privacy.hv-privacy-studio {
    max-width: 1400px;
    min-height: calc(100vh - 64px);
    margin-left: auto;
    margin-right: auto;
    padding: 16px 16px 28px;
    display: flex;
    flex-direction: column;
  }
  @media (min-width: 768px) {
    .hv-privacy.hv-privacy-studio {
      padding-left: 24px;
      padding-right: 24px;
    }
  }
  .hv-privacy-studio-shell {
    position: relative;
    flex: 1;
    display: grid;
    grid-template-columns: 280px minmax(0, 1fr);
    min-height: calc(100vh - 108px);
    overflow: hidden;
    border-radius: 18px;
    background: var(--bg-surface);
    border: 1px solid var(--border-default);
    box-shadow: var(--shadow-md);
  }
  .hv-privacy-studio-shell::before {
    content: '';
    position: absolute;
    left: 0; right: 0; top: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--teal), var(--teal-2), transparent 70%);
    pointer-events: none;
    z-index: 1;
  }
  .hv-privacy-rail,
  .hv-privacy-canvas {
    display: flex;
    flex-direction: column;
    min-height: 0;
    background: var(--bg-surface);
    color: var(--text-primary);
  }
  .hv-privacy-rail {
    border-right: 1px solid var(--border-default);
  }
  .hv-privacy-rail__top {
    padding: 18px 16px 14px;
    border-bottom: 1px solid var(--border-subtle);
  }
  .hv-privacy-rail__mark {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--navy);
  }
  :root[data-theme="dark"] .hv-privacy-rail__mark { color: var(--teal); }
  .hv-privacy-rail__dot {
    width: 8px;
    height: 8px;
    border-radius: 99px;
    background: var(--teal);
    box-shadow: 0 0 0 4px color-mix(in srgb, var(--teal) 28%, transparent);
  }
  .hv-privacy-mode {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px;
    padding: 3px;
    border-radius: 12px;
    background: var(--bg-subtle);
    border: 1px solid var(--border-default);
  }
  .hv-privacy-mode button {
    border: 0;
    background: transparent;
    color: var(--text-muted);
    font: inherit;
    font-size: 12px;
    font-weight: 700;
    padding: 8px 6px;
    border-radius: 9px;
    cursor: pointer;
  }
  .hv-privacy-mode button.is-on {
    background: var(--navy);
    color: #fff;
    box-shadow: var(--shadow-sm);
  }
  :root[data-theme="dark"] .hv-privacy-mode button.is-on {
    background: var(--teal);
    color: var(--navy);
  }
  .hv-privacy-rail__counts {
    margin: 12px 0 0;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: var(--text-muted);
  }
  .hv-privacy-rail__counts span { margin: 0 6px; opacity: 0.45; }
  .hv-privacy-rail__actions { padding: 12px 14px 6px; }
  .hv-privacy-rail__actions .hv-privacy-btn { width: 100%; }
  .hv-privacy-studio .hv-privacy-list {
    flex: 1;
    max-height: none;
    padding: 8px 12px 18px;
  }
  .hv-privacy-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
  .hv-privacy-group__label {
    margin: 0 6px 4px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-muted);
  }
  .hv-privacy-studio .hv-privacy-list-item {
    align-items: center;
    border-radius: 12px;
    background: var(--bg-subtle);
    border-color: transparent;
  }
  .hv-privacy-studio .hv-privacy-list-item:hover {
    background: color-mix(in srgb, var(--teal) 12%, var(--bg-subtle));
    border-color: color-mix(in srgb, var(--teal) 35%, var(--border-default));
  }
  .hv-privacy-studio .hv-privacy-list-item.is-active {
    background: color-mix(in srgb, var(--teal) 16%, var(--bg-surface));
    border-color: color-mix(in srgb, var(--teal) 55%, var(--border-default));
    box-shadow: inset 3px 0 0 var(--teal);
  }
  .hv-privacy-studio .hv-privacy-list-item.is-live .hv-privacy-list-item__name::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    margin-right: 6px;
    margin-bottom: 1px;
    border-radius: 99px;
    background: var(--text-success);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--text-success) 22%, transparent);
  }
  .hv-privacy-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 22px;
    background: var(--bg-surface);
    border-bottom: 1px solid var(--border-subtle);
  }
  .hv-privacy-toolbar__kicker {
    margin: 0 0 2px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--teal-2);
  }
  .hv-privacy-toolbar__title {
    margin: 0;
    font-size: 20px;
    font-weight: 750;
    letter-spacing: -0.03em;
    color: var(--text-primary);
  }
  .hv-privacy-canvas__alerts { padding: 12px 22px 0; }
  .hv-privacy-canvas__alerts .hv-privacy-alert { margin-bottom: 0; }
  .hv-privacy-banner {
    margin: 14px 22px 0;
    padding: 11px 14px;
    border-radius: 12px;
    font-size: 13px;
    line-height: 1.5;
  }
  .hv-privacy-banner--draft {
    background: color-mix(in srgb, var(--teal) 14%, var(--bg-surface));
    border: 1px solid color-mix(in srgb, var(--teal) 36%, var(--border-default));
    color: var(--text-primary);
  }
  .hv-privacy-banner--live {
    background: var(--warning-light);
    border: 1px solid var(--warning-border);
    color: var(--text-primary);
  }
  .hv-privacy-banner--new {
    background: var(--info-light);
    border: 1px solid var(--info-border);
    color: var(--text-primary);
  }
  .hv-privacy-canvas__body {
    flex: 1;
    overflow: auto;
    padding: 18px 22px 28px;
  }
  .hv-privacy-paper {
    padding: 22px 22px 26px;
    border-radius: 16px;
    background: var(--bg-subtle);
    border: 1px solid var(--border-subtle);
  }
  .hv-privacy-studio .hv-privacy-switch-card,
  .hv-privacy-studio .hv-privacy-control,
  .hv-privacy-studio select.hv-privacy-control,
  .hv-privacy-studio textarea.hv-privacy-control {
    background: var(--bg-surface);
  }
  .hv-privacy textarea.hv-privacy-control--body { min-height: 180px; }
  .hv-privacy.hv-privacy-board {
    max-width: 1400px;
    min-height: calc(100vh - 64px);
    margin-left: auto;
    margin-right: auto;
    padding: 16px 16px 32px;
  }
  @media (min-width: 768px) {
    .hv-privacy.hv-privacy-board {
      padding-left: 24px;
      padding-right: 24px;
    }
  }
  .hv-privacy-board__head { margin-bottom: 12px; }
  .hv-privacy-board__title {
    margin: 0;
    font-size: 22px;
    font-weight: 750;
    letter-spacing: -0.03em;
    color: var(--text-primary);
  }
  .hv-privacy-board__sub {
    margin: 4px 0 0;
    max-width: 560px;
    font-size: 13px;
    line-height: 1.45;
    color: var(--text-muted);
  }
  .hv-privacy-metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 12px;
  }
  .hv-privacy-metric {
    position: relative;
    overflow: hidden;
    padding: 8px 12px 8px 14px;
    border-radius: 10px;
    background: var(--bg-surface);
    border: 1px solid var(--border-default);
  }
  .hv-privacy-metric::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 3px;
    background: var(--teal);
  }
  .hv-privacy-board .hv-privacy-stat__k { font-size: 10px; }
  .hv-privacy-board .hv-privacy-stat__v { margin-top: 2px; font-size: 13px; }
  .hv-privacy-board .hv-privacy-stack { gap: 10px; }
  .hv-privacy-board .hv-privacy-two { gap: 10px; align-items: stretch; }
  .hv-privacy-board .hv-privacy-section {
    border-radius: 12px;
    box-shadow: none;
  }
  .hv-privacy-board .hv-privacy-section__head { padding: 10px 14px 8px; }
  .hv-privacy-board .hv-privacy-section__head-row { gap: 8px; }
  .hv-privacy-board .hv-privacy-section__icon {
    width: 26px;
    height: 26px;
    border-radius: 8px;
  }
  .hv-privacy-board .hv-privacy-section__title { font-size: 14px; }
  .hv-privacy-board .hv-privacy-section__hint { margin-top: 2px; font-size: 12px; }
  .hv-privacy-board .hv-privacy-section__body { padding: 8px 12px 12px; gap: 8px; }
  .hv-privacy-board .hv-privacy-row {
    padding: 8px 10px;
    border-radius: 10px;
    gap: 8px;
  }
  .hv-privacy-board .hv-privacy-row__title { font-size: 13px; }
  .hv-privacy-board .hv-privacy-choice {
    padding: 8px 10px;
    padding-right: 32px;
    gap: 2px;
    border-radius: 10px;
  }
  .hv-privacy-board .hv-privacy-choice__title { font-size: 13px; }
  .hv-privacy-board .hv-privacy-choice__desc { font-size: 12px; line-height: 1.35; }
  .hv-privacy-board .hv-privacy-choice__tick {
    top: 8px;
    right: 8px;
    width: 18px;
    height: 18px;
  }
  .hv-privacy-board .hv-privacy-choice-grid { gap: 8px; }
  .hv-privacy-board .hv-privacy-rights { gap: 20px; }
  .hv-privacy-board .hv-privacy-right {
    padding: 10px 12px;
    border-radius: 10px;
  }
  .hv-privacy-board .hv-privacy-right__icon {
    width: 28px;
    height: 28px;
    margin-bottom: 6px;
    border-radius: 8px;
  }
  .hv-privacy-board .hv-privacy-right__btn { margin-top: 8px; align-self: flex-start; }
  .hv-privacy-board .hv-privacy-alert { margin-bottom: 10px; padding: 10px 12px; }
  .hv-privacy-board .hv-privacy-empty { padding: 18px 12px; }
  .hv-privacy-board .hv-privacy-timeline li { padding: 8px 2px; }
  .hv-privacy-legal { display: flex; flex-direction: column; gap: 8px; }
  .hv-privacy-doc--inline {
    max-height: 240px;
    padding: 12px;
    font-size: 13px;
  }
  .hv-privacy-sheet-backdrop {
    position: fixed;
    inset: 0;
    z-index: var(--z-overlay);
    border: 0;
    background: rgba(0, 0, 0, 0.4);
    cursor: default;
  }
  .hv-privacy-sheet {
    position: fixed;
    top: 0;
    left: 0;
    z-index: var(--z-modal);
    display: flex;
    flex-direction: column;
    height: 100%;
    width: min(84vw, 300px);
    overflow: hidden;
    background: var(--bg-surface);
    border-right: 1px solid var(--border-default);
    box-shadow: var(--shadow-xl);
  }
  .hv-privacy-sheet .hv-privacy-rail {
    flex: 1;
    min-height: 0;
    border-right: 0;
  }
  .hv-privacy-sheet__close {
    position: absolute;
    top: 12px;
    right: 10px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: var(--text-muted);
    cursor: pointer;
  }
  .hv-privacy-sheet__close:hover { background: var(--bg-subtle); color: var(--text-primary); }
  .hv-privacy-nav-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }
  .hv-privacy-nav-row svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: var(--teal-2);
  }
  .hv-privacy-studio .hv-privacy-row,
  .hv-privacy-studio .hv-privacy-right,
  .hv-privacy-studio .hv-privacy-choice,
  .hv-privacy-studio .hv-privacy-empty {
    background: var(--bg-surface);
  }
  .hv-privacy-studio fieldset { border: 0; margin: 0; padding: 0; min-width: 0; }

  .hv-privacy-rail__lede {
    margin: 0 0 12px;
    font-size: 12px;
    line-height: 1.45;
    color: var(--text-muted);
  }
  .hv-privacy-list-item__main {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    min-width: 0;
  }
  .hv-privacy-list-item__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: 8px;
    background: color-mix(in srgb, var(--teal) 18%, transparent);
    color: var(--navy);
  }
  :root[data-theme="dark"] .hv-privacy-list-item__icon { color: var(--teal); }
  .hv-privacy-list-item__copy { min-width: 0; }
  .hv-privacy-canvas__loading {
    display: flex;
    min-height: 240px;
    align-items: center;
    justify-content: center;
  }
  .hv-privacy-settings .hv-privacy-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    overflow: auto;
    padding: 12px;
  }
  .hv-privacy-settings .hv-privacy-settings-paper {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .hv-privacy-settings .hv-privacy-row {
    padding: 10px 12px;
    border-radius: 10px;
    gap: 8px;
    background: var(--bg-surface);
  }
  .hv-privacy-settings .hv-privacy-row__title { font-size: 13px; }
  .hv-privacy-settings .hv-privacy-choice {
    padding: 10px 12px;
    padding-right: 34px;
    gap: 2px;
    border-radius: 10px;
  }
  .hv-privacy-settings .hv-privacy-choice__title { font-size: 13px; }
  .hv-privacy-settings .hv-privacy-choice__desc { font-size: 12px; line-height: 1.35; }
  .hv-privacy-settings .hv-privacy-choice__tick {
    top: 8px;
    right: 8px;
    width: 18px;
    height: 18px;
  }
  .hv-privacy-settings .hv-privacy-choice-grid { gap: 8px; }
  .hv-privacy-settings .hv-privacy-rights { gap: 20px; }
  .hv-privacy-settings .hv-privacy-right {
    padding: 12px;
    border-radius: 10px;
    background: var(--bg-surface);
  }
  .hv-privacy-settings .hv-privacy-right__icon {
    width: 28px;
    height: 28px;
    margin-bottom: 6px;
    border-radius: 8px;
  }
  .hv-privacy-settings .hv-privacy-right__btn { margin-top: 8px; align-self: flex-start; }
  .hv-privacy-settings .hv-privacy-empty { padding: 18px 12px; }
  .hv-privacy-settings .hv-privacy-timeline li { padding: 8px 2px; }
  .hv-privacy-settings .hv-privacy-sheet .hv-privacy-rail__top { padding-right: 44px; }

  .hv-privacy-hero {
    position: relative;
    overflow: hidden;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 22px;
    padding: 28px 28px 24px;
    border-radius: var(--radius-xl);
    background:
      radial-gradient(1200px 280px at 100% -20%, color-mix(in srgb, var(--teal) 38%, transparent), transparent 55%),
      linear-gradient(135deg, var(--navy) 0%, var(--navy-3) 62%, var(--navy-2) 100%);
    color: #fff;
    box-shadow: var(--shadow-lg);
  }
  .hv-privacy-hero::after {
    content: '';
    position: absolute;
    left: 0; right: 0; bottom: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--teal), var(--teal-2), transparent);
  }
  .hv-privacy-hero__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 16px;
    background: color-mix(in srgb, var(--teal) 88%, white);
    color: var(--navy);
    box-shadow: 0 8px 20px color-mix(in srgb, var(--teal) 35%, transparent);
  }
  .hv-privacy-hero__eyebrow {
    margin: 0 0 4px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--teal-3);
  }
  .hv-privacy-hero__title {
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: #fff;
  }
  .hv-privacy-hero__sub {
    margin: 8px 0 0;
    max-width: 540px;
    font-size: 14px;
    line-height: 1.55;
    color: color-mix(in srgb, #fff 78%, var(--teal-3));
  }
  .hv-privacy-hero__row {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }

  .hv-privacy-steps {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 20px;
  }
  .hv-privacy-step {
    padding: 14px 16px;
    border-radius: var(--radius-lg);
    background: var(--bg-surface);
    border: 1px solid var(--border-default);
    box-shadow: var(--shadow-sm);
  }
  .hv-privacy-step__n {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    margin-bottom: 8px;
    border-radius: var(--radius-full);
    background: color-mix(in srgb, var(--teal) 28%, transparent);
    color: var(--navy);
    font-size: 11px;
    font-weight: 800;
  }
  :root[data-theme="dark"] .hv-privacy-step__n { color: var(--teal); }
  .hv-privacy-step__t {
    margin: 0;
    font-size: 13px;
    font-weight: 650;
    color: var(--text-primary);
  }
  .hv-privacy-step__d {
    margin: 4px 0 0;
    font-size: 12px;
    line-height: 1.45;
    color: var(--text-muted);
  }

  .hv-privacy-tabs {
    display: inline-flex;
    gap: 6px;
    padding: 4px;
    border-radius: var(--radius-full);
    background: color-mix(in srgb, #fff 12%, transparent);
    border: 1px solid color-mix(in srgb, #fff 16%, transparent);
  }
  .hv-privacy-tab {
    border: 0;
    cursor: pointer;
    padding: 8px 16px;
    border-radius: var(--radius-full);
    background: transparent;
    color: color-mix(in srgb, #fff 80%, transparent);
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    transition: background var(--transition-normal), color var(--transition-normal);
  }
  .hv-privacy-tab.is-active {
    background: var(--teal);
    color: var(--navy);
  }

  .hv-privacy-alert {
    margin-bottom: 16px;
    padding: 12px 16px;
    border-radius: var(--radius-md);
    font-size: 14px;
    line-height: 1.45;
  }
  .hv-privacy-alert--error {
    background: var(--error-light);
    border: 1px solid var(--error-border);
    color: var(--text-danger);
  }
  .hv-privacy-alert--ok {
    background: var(--success-light);
    border: 1px solid var(--success-border);
    color: var(--text-success);
  }
  .hv-privacy-alert--warn {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: var(--warning-light);
    border: 1px solid var(--warning-border);
    color: var(--text-primary);
  }

  .hv-privacy-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 40px;
    padding: 0 16px;
    border: 1px solid transparent;
    border-radius: var(--radius-md);
    background: var(--interactive-primary);
    color: var(--interactive-primary-text);
    font: inherit;
    font-size: 13px;
    font-weight: 650;
    cursor: pointer;
    transition: background var(--transition-normal), transform var(--transition-fast), box-shadow var(--transition-normal);
  }
  .hv-privacy-btn:hover:not(:disabled) {
    background: var(--interactive-primary-hover);
    box-shadow: var(--shadow-sm);
  }
  .hv-privacy-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .hv-privacy-btn--teal {
    background: var(--teal);
    color: var(--navy);
  }
  .hv-privacy-btn--teal:hover:not(:disabled) { background: var(--teal-2); }
  .hv-privacy-btn--ghost {
    background: var(--bg-surface);
    color: var(--text-primary);
    border-color: var(--border-default);
  }
  .hv-privacy-btn--ghost:hover:not(:disabled) {
    background: var(--bg-subtle);
    border-color: var(--navy);
  }
  .hv-privacy-btn--danger {
    background: transparent;
    color: var(--text-danger);
    border-color: var(--error-border);
  }
  .hv-privacy-btn--danger:hover:not(:disabled) { background: var(--error-light); }
  .hv-privacy-btn--sm { min-height: 34px; padding: 0 12px; font-size: 12px; }
  .hv-privacy-actions { display: flex; flex-wrap: wrap; gap: 8px; }

  .hv-privacy-workspace {
    display: grid;
    grid-template-columns: minmax(280px, 340px) minmax(0, 1fr);
    gap: 20px;
    align-items: start;
  }
  .hv-privacy-panel {
    background: var(--bg-surface);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-md);
    overflow: hidden;
  }
  .hv-privacy-panel__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 18px 20px 14px;
    border-bottom: 1px solid var(--border-subtle);
    background: linear-gradient(180deg, color-mix(in srgb, var(--teal-50) 80%, var(--bg-surface)), var(--bg-surface));
  }
  :root[data-theme="dark"] .hv-privacy-panel__head {
    background: linear-gradient(180deg, color-mix(in srgb, var(--teal) 10%, var(--bg-surface)), var(--bg-surface));
  }
  .hv-privacy-panel__title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: var(--text-primary);
  }
  .hv-privacy-panel__hint {
    margin: 4px 0 0;
    font-size: 12px;
    line-height: 1.45;
    color: var(--text-muted);
  }
  .hv-privacy-panel__body { padding: 16px 20px 20px; }
  .hv-privacy-list {
    max-height: calc(100vh - 260px);
    overflow: auto;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .hv-privacy-list-item {
    width: 100%;
    text-align: left;
    cursor: pointer;
    border: 1px solid var(--border-default);
    background: var(--bg-surface);
    border-radius: var(--radius-md);
    padding: 12px 14px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    transition: border-color var(--transition-normal), background var(--transition-normal), box-shadow var(--transition-normal);
  }
  .hv-privacy-list-item:hover {
    border-color: color-mix(in srgb, var(--teal) 55%, var(--border-default));
    background: var(--teal-50);
  }
  :root[data-theme="dark"] .hv-privacy-list-item:hover {
    background: color-mix(in srgb, var(--teal) 12%, transparent);
  }
  .hv-privacy-list-item.is-active {
    border-color: var(--navy);
    background: color-mix(in srgb, var(--teal) 16%, var(--bg-surface));
    box-shadow: inset 3px 0 0 var(--teal);
  }
  .hv-privacy-list-item__name {
    margin: 0;
    font-size: 13px;
    font-weight: 650;
    color: var(--text-primary);
  }
  .hv-privacy-list-item__meta {
    margin: 3px 0 0;
    font-size: 11px;
    color: var(--text-muted);
  }

  .hv-privacy-badge {
    display: inline-flex;
    align-items: center;
    padding: 1px 7px;
    border-radius: var(--radius-full);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.02em;
    white-space: nowrap;
  }
  .hv-privacy-badge--ok,
  .hv-privacy-badge--PUBLISHED { background: var(--success-light); color: var(--text-success); }
  .hv-privacy-badge--warn,
  .hv-privacy-badge--DRAFT { background: var(--warning-light); color: var(--text-warning); }
  .hv-privacy-badge--danger,
  .hv-privacy-badge--RETIRED { background: var(--error-light); color: var(--text-danger); }
  .hv-privacy-badge--muted { background: var(--bg-muted); color: var(--text-muted); }

  .hv-privacy-field { display: flex; flex-direction: column; gap: 6px; }
  .hv-privacy-label {
    font-size: 13px;
    font-weight: 650;
    color: var(--text-primary);
  }
  .hv-privacy-hint { margin: 0; font-size: 12px; color: var(--text-muted); }
  .hv-privacy-control,
  .hv-privacy select.hv-privacy-control,
  .hv-privacy textarea.hv-privacy-control {
    width: 100%;
    min-height: 42px;
    padding: 10px 12px;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
    background: var(--control-bg);
    color: var(--control-text);
    font: inherit;
    font-size: 14px;
  }
  .hv-privacy textarea.hv-privacy-control { min-height: 96px; resize: vertical; }
  .hv-privacy textarea.hv-privacy-md {
    min-height: 220px;
    font-family: var(--font-mono);
    font-size: 12.5px;
    line-height: 1.55;
  }
  @media (min-width: 768px) {
    .hv-privacy textarea.hv-privacy-md { min-height: 280px; }
  }
  .hv-privacy-summary-quill .ql-snow .ql-picker.ql-expanded .ql-picker-options {
    z-index: 40;
    left: auto;
    right: 0;
  }
  .hv-privacy textarea.hv-privacy-control--code {
    min-height: 140px;
    font-family: var(--font-mono);
    font-size: 12px;
  }
  .hv-privacy-control:focus {
    outline: none;
    border-color: var(--teal-2);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--teal) 28%, transparent);
  }
  .hv-privacy-search {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 44px;
    padding: 0 12px;
  }
  .hv-privacy-search svg { flex-shrink: 0; color: var(--text-muted); }
  .hv-privacy-search input {
    flex: 1;
    min-width: 0;
    width: 100%;
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--control-text);
    font: inherit;
    font-size: 14px;
    line-height: 1.4;
    outline: none;
  }
  .hv-privacy-search input::placeholder { color: var(--text-muted); }
  .hv-privacy-search:focus-within {
    border-color: var(--teal-2);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--teal) 28%, transparent);
  }
  .hv-privacy-search--compact {
    min-height: 34px;
    gap: 6px;
    padding: 0 10px;
  }
  .hv-privacy-search--compact input { font-size: 13px; }
  .hv-privacy-scroll {
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .hv-privacy-scroll::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
  .hv-privacy-activity {
    max-height: calc(32px * 4 + 2px);
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  .hv-privacy-control:disabled { opacity: 0.65; cursor: not-allowed; }
  .hv-privacy-select-wrap { width: 100%; min-width: 0; max-width: 100%; }
  .hv-privacy-select-wrap > * { min-width: 0; max-width: 100%; }
  button.hv-privacy-select {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
    max-width: 100%;
    width: 100%;
    text-align: left;
    cursor: pointer;
  }
  button.hv-privacy-select > span:first-child {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  button.hv-privacy-select svg {
    flex-shrink: 0;
    color: var(--text-muted);
  }
  button.hv-privacy-select[data-state="open"] {
    border-color: var(--interactive-primary);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--teal) 28%, transparent);
  }
  .hv-privacy-summary-quill .ql-snow .ql-picker-options {
    max-width: min(90vw, 280px);
    max-height: min(240px, 50vh);
    overflow: auto;
  }
  .hv-privacy-grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }
  .hv-privacy-form { display: flex; flex-direction: column; gap: 16px; }
  .hv-privacy-switch-card {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px 16px;
    border-radius: var(--radius-lg);
    border: 1px solid var(--border-default);
    background: var(--bg-subtle);
  }
  .hv-privacy-switch-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    font-size: 14px;
  }

  .hv-privacy-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 36px 20px;
    border: 1px dashed var(--border-default);
    border-radius: var(--radius-lg);
    background: var(--bg-subtle);
  }
  .hv-privacy-empty__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    margin-bottom: 10px;
    border-radius: var(--radius-full);
    background: color-mix(in srgb, var(--teal) 22%, transparent);
    color: var(--navy);
  }
  :root[data-theme="dark"] .hv-privacy-empty__icon { color: var(--teal); }
  .hv-privacy-empty__title { margin: 0; font-weight: 650; color: var(--text-primary); }
  .hv-privacy-empty__body { margin: 6px 0 0; max-width: 360px; font-size: 13px; color: var(--text-muted); }

  .hv-privacy-section {
    background: var(--bg-surface);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-md);
    overflow: hidden;
  }
  .hv-privacy-section__head {
    padding: 18px 22px 12px;
    border-bottom: 1px solid var(--border-subtle);
  }
  .hv-privacy-section__head-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .hv-privacy-section__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: color-mix(in srgb, var(--teal) 22%, transparent);
    color: var(--navy);
  }
  :root[data-theme="dark"] .hv-privacy-section__icon { color: var(--teal); }
  .hv-privacy-section__title { margin: 0; font-size: 16px; font-weight: 700; }
  .hv-privacy-section__hint { margin: 6px 0 0; font-size: 13px; color: var(--text-muted); }
  .hv-privacy-section__body { padding: 16px 22px 20px; display: flex; flex-direction: column; gap: 10px; }

  .hv-privacy-stack { display: flex; flex-direction: column; gap: 18px; }
  .hv-privacy-two {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
    align-items: start;
  }

  .hv-privacy-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border-radius: var(--radius-lg);
    border: 1px solid var(--border-default);
    background: var(--bg-subtle);
  }
  .hv-privacy-row__title { margin: 0; font-size: 14px; font-weight: 650; }
  .hv-privacy-row__meta { margin: 3px 0 0; font-size: 12px; color: var(--text-muted); }

  .hv-privacy-choice-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .hv-privacy-choice {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 6px;
    cursor: pointer;
    padding: 16px;
    border-radius: var(--radius-lg);
    border: 1.5px solid var(--border-default);
    background: var(--bg-surface);
    transition: border-color var(--transition-normal), background var(--transition-normal), box-shadow var(--transition-normal);
  }
  .hv-privacy-choice:hover { border-color: color-mix(in srgb, var(--teal) 50%, var(--border-default)); }
  .hv-privacy-choice.is-on {
    border-color: var(--navy);
    background: color-mix(in srgb, var(--teal) 14%, var(--bg-surface));
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--teal) 22%, transparent);
  }
  :root[data-theme="dark"] .hv-privacy-choice.is-on { border-color: var(--teal); }
  .hv-privacy-choice__copy {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
  }
  .hv-privacy-choice__title {
    display: block;
    font-size: 14px;
    font-weight: 700;
    color: var(--text-primary);
    line-height: 1.3;
  }
  .hv-privacy-choice__desc {
    display: block;
    font-size: 13px;
    line-height: 1.45;
    color: var(--text-muted);
  }
  .hv-privacy-choice__tick {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 22px;
    height: 22px;
    border-radius: var(--radius-full);
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--teal);
    color: var(--navy);
  }

  .hv-privacy-opt {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
    padding: 12px 14px;
    border-radius: var(--radius-md);
    border: 1px solid var(--border-default);
    background: var(--bg-surface);
  }
  .hv-privacy-opt:hover { background: var(--bg-subtle); }
  .hv-privacy-opt.is-on {
    border-color: color-mix(in srgb, var(--teal) 60%, var(--border-default));
    background: color-mix(in srgb, var(--teal) 10%, var(--bg-surface));
  }
  .hv-privacy-rights {
    display: flex;
    flex-direction: column;
    gap: 22px;
  }
  .hv-privacy-rights--compact {
    gap: 14px;
  }
  .hv-privacy-rights--compact .hv-privacy-dsar-row {
    min-height: 52px;
    padding: 8px 12px;
    gap: 10px;
  }
  .hv-privacy-rights--compact .hv-privacy-dsar-row__icon {
    width: 26px;
    height: 26px;
    border-radius: 7px;
  }
  .hv-privacy-rights--compact .hv-privacy-dsar-row__action {
    font-size: 11px;
  }
  .hv-privacy-dsar-group__title {
    margin: 0;
    font-size: 13px;
    font-weight: 650;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--text-muted);
  }
  .hv-privacy-dsar-group__hint {
    margin: 4px 0 8px;
    font-size: 12px;
    line-height: 1.4;
    color: var(--text-muted);
  }
  .hv-privacy-dsar-list {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--border-default);
    border-radius: 12px;
    background: var(--bg-surface);
  }
  .hv-privacy-dsar-list--danger {
    border-color: color-mix(in srgb, var(--text-danger) 28%, var(--border-default));
    background: var(--error-light);
  }
  .hv-privacy-dsar-row {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    border: 0;
    border-top: 1px solid var(--border-subtle);
    border-radius: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
    transition: background var(--transition-normal);
  }
  .hv-privacy-dsar-row:first-child { border-top: 0; }
  .hv-privacy-dsar-row:hover:not(:disabled) { background: var(--bg-subtle); }
  .hv-privacy-dsar-row:focus-visible {
    outline: 2px solid var(--interactive-primary);
    outline-offset: -2px;
  }
  .hv-privacy-dsar-row:disabled { opacity: 0.55; cursor: not-allowed; }
  .hv-privacy-dsar-list--danger .hv-privacy-dsar-row:hover:not(:disabled) {
    background: var(--error-lighter);
  }
  .hv-privacy-dsar-row__icon {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: color-mix(in srgb, var(--interactive-primary) 12%, transparent);
    color: var(--interactive-primary);
  }
  .hv-privacy-dsar-list--danger .hv-privacy-dsar-row__icon {
    background: color-mix(in srgb, var(--text-danger) 12%, transparent);
    color: var(--text-danger);
  }
  .hv-privacy-dsar-row__action {
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 700;
    color: var(--interactive-primary);
  }
  .hv-privacy-dsar-list--danger .hv-privacy-dsar-row__action { color: var(--text-danger); }
  .hv-privacy-kv {
    overflow: hidden;
    border: 1px solid var(--border-default);
    border-radius: 12px;
    background: var(--bg-surface);
  }
  .hv-privacy-kv__row {
    display: grid;
    grid-template-columns: minmax(110px, 34%) 1fr;
    gap: 12px;
    padding: 10px 14px;
    border-top: 1px solid var(--border-subtle);
  }
  .hv-privacy-kv__row:first-child { border-top: 0; }
  .hv-privacy-kv__k {
    margin: 0;
    font-size: 12px;
    color: var(--text-muted);
  }
  .hv-privacy-kv__v {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
    overflow-wrap: anywhere;
  }
  .hv-privacy-panel {
    padding: 12px 14px;
    border: 1px solid var(--border-default);
    border-radius: 12px;
    background: var(--bg-subtle);
  }
  .hv-privacy-panel--danger {
    border-color: color-mix(in srgb, var(--text-danger) 28%, var(--border-default));
    background: var(--error-light);
    color: var(--text-primary);
  }
  .hv-privacy-field-input,
  .hv-privacy-field-input:disabled {
    width: 100%;
    height: 40px;
    padding: 0 12px;
    border: 1px solid var(--border-default);
    border-radius: 10px;
    background: var(--control-bg);
    color: var(--text-primary);
    font: inherit;
    font-size: 13px;
  }
  textarea.hv-privacy-field-input {
    height: auto;
    min-height: 128px;
    padding: 10px 12px;
    resize: vertical;
    line-height: 1.45;
  }
  .hv-privacy-field-input:focus {
    outline: none;
    border-color: var(--interactive-primary);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--interactive-primary) 16%, transparent);
  }
  .hv-privacy-field-input:disabled {
    background: var(--bg-subtle);
    color: var(--text-muted);
    cursor: not-allowed;
  }
  @media (max-width: 560px) {
    .hv-privacy-kv__row { grid-template-columns: 1fr; gap: 2px; padding: 10px 12px; }
  }
  .hv-privacy-right {
    display: flex;
    flex-direction: column;
    padding: 16px;
    border-radius: var(--radius-lg);
    border: 1px solid var(--border-default);
    background: var(--bg-subtle);
  }
  .hv-privacy-right__icon {
    width: 36px;
    height: 36px;
    margin-bottom: 10px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--navy);
    color: var(--teal);
  }
  .hv-privacy-timeline { list-style: none; margin: 0; padding: 0; }
  .hv-privacy-timeline li {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 4px;
    border-bottom: 1px solid var(--border-subtle);
  }
  .hv-privacy-timeline li:last-child { border-bottom: 0; }
  .hv-privacy-lookup-search { display: flex; gap: 8px; margin-bottom: 20px; }
  .hv-privacy-lookup-search .hv-privacy-control { flex: 1; }
  .hv-privacy-person {
    padding: 16px 18px;
    border-radius: var(--radius-lg);
    background: linear-gradient(135deg, var(--navy), var(--navy-3));
    color: #fff;
  }
  .hv-privacy-person p { margin: 0; }
  .hv-privacy-person__id { margin-top: 4px; font-size: 12px; color: var(--teal-3); }
  .hv-privacy-stat {
    padding: 14px 16px;
    border-radius: var(--radius-md);
    border: 1px solid var(--border-default);
    background: var(--bg-subtle);
  }
  .hv-privacy-stat__k {
    margin: 0;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--text-muted);
  }
  .hv-privacy-stat__v { margin: 6px 0 0; font-size: 14px; font-weight: 650; }
  .hv-privacy-doc {
    max-height: 320px;
    overflow: auto;
    padding: 16px;
    border-radius: var(--radius-md);
    border: 1px solid var(--border-default);
    background: var(--bg-subtle);
    font-size: 14px;
    line-height: 1.65;
  }
  .hv-legal-summary,
  .hv-legal-summary * {
    max-width: 100%;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .hv-legal-summary {
    min-width: 0;
    max-width: 100%;
    overflow-wrap: anywhere;
    word-break: break-word;
    white-space: normal;
  }
  .hv-legal-summary p { margin: 0 0 0.65em; }
  .hv-legal-summary p:last-child { margin-bottom: 0; }
  .hv-legal-summary ul,
  .hv-legal-summary ol { margin: 0 0 0.65em; padding-left: 1.15rem; }
  .hv-legal-summary pre,
  .hv-legal-article pre {
    margin: 0 0 0.75em;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    word-break: break-word;
    max-width: 100%;
  }
  .hv-legal-article {
    color: var(--text-primary);
    font-size: 15px;
    line-height: 1.75;
    min-width: 0;
    max-width: 100%;
    overflow-wrap: break-word;
  }
  .hv-legal-article img,
  .hv-legal-article video,
  .hv-legal-article iframe {
    max-width: 100%;
    height: auto;
  }
  .hv-legal-article pre {
    margin: 0 0 0.75em;
    max-width: 100%;
    overflow-x: auto;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .hv-legal-article p { margin: 0 0 1em; }
  .hv-legal-article p:last-child { margin-bottom: 0; }
  .hv-legal-article h1,
  .hv-legal-article h2,
  .hv-legal-article h3,
  .hv-legal-article h4 {
    margin: 1.4em 0 0.5em;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--text-primary);
    line-height: 1.3;
  }
  .hv-legal-article h1 { font-size: 1.35rem; margin-top: 0; }
  .hv-legal-article h2 { font-size: 1.15rem; }
  .hv-legal-article h3 { font-size: 1.02rem; }
  .hv-legal-article ul,
  .hv-legal-article ol { margin: 0 0 1em; padding-left: 1.25rem; }
  .hv-legal-article li { margin: 0 0 0.4em; }
  .hv-legal-article a { color: var(--interactive-primary); }
  .hv-legal-article strong { font-weight: 700; }
  .hv-legal-article em { font-style: italic; }
  .hv-legal-article blockquote {
    margin: 0 0 1em;
    padding: 0 1em;
    border-left: 0.25em solid var(--border-default);
    color: var(--text-secondary);
  }
  .hv-legal-article hr {
    margin: 1.25em 0;
    border: 0;
    border-top: 1px solid var(--border-default);
  }
  .hv-legal-table-wrap {
    width: 100%;
    overflow-x: auto;
    margin: 0 0 1.25em;
  }
  .hv-legal-article table {
    width: 100%;
    border-collapse: collapse;
    margin: 0;
  }
  .hv-legal-article th,
  .hv-legal-article td {
    padding: 6px 13px;
    border: 1px solid var(--border-default);
    text-align: left;
    vertical-align: top;
  }
  .hv-legal-article th { font-weight: 700; }
  .hv-privacy-accept-bar {
    position: fixed;
    left: 0; right: 0; bottom: 0;
    z-index: var(--z-sticky);
    padding: 12px 0;
    border-top: 1px solid var(--border-default);
    background: color-mix(in srgb, var(--bg-surface) 92%, transparent);
    backdrop-filter: blur(10px);
  }
  .hv-privacy-accept-bar__inner {
    max-width: 1400px;
    margin: 0 auto;
    padding-left: 16px;
    padding-right: 16px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  @media (min-width: 768px) {
    .hv-privacy-accept-bar__inner { padding-left: 24px; padding-right: 24px; }
  }
  .hv-privacy-gate {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: calc(100vh - 64px);
    padding: 24px 16px 32px;
    background:
      radial-gradient(900px 280px at 50% -40px, color-mix(in srgb, var(--teal) 16%, transparent), transparent 60%),
      var(--bg-page);
  }
  .hv-privacy-gate-modal {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 640px;
    max-height: min(82vh, 760px);
    overflow: hidden;
    border-radius: 18px;
    background: var(--bg-surface);
    border: 1px solid var(--border-default);
    box-shadow: var(--shadow-xl);
  }
  .hv-privacy-gate-modal::before {
    content: '';
    position: absolute;
    left: 0; right: 0; top: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--teal), var(--teal-2), transparent 75%);
  }
  .hv-privacy-gate-modal__head {
    padding: 18px 18px 12px;
    border-bottom: 1px solid var(--border-subtle);
  }
  .hv-privacy-gate-modal__title {
    margin: 0 0 4px;
    font-size: 22px;
    font-weight: 750;
    letter-spacing: -0.03em;
    color: var(--text-primary);
  }
  .hv-privacy-gate-modal__body {
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 12px 18px 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .hv-privacy-gate-doc {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .hv-privacy-gate-doc__head {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
  }
  .hv-privacy-doc--gate {
    max-height: none;
    min-height: 0;
    padding: 12px 14px;
    font-size: 13px;
    line-height: 1.6;
  }
  .hv-privacy-gate-modal__foot {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 18px 16px;
    border-top: 1px solid var(--border-subtle);
    background: var(--bg-surface);
  }
  .hv-privacy-apply {
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
  }
  .hv-privacy-apply fieldset { border: 0; margin: 0; padding: 0; min-width: 0; }
  .hv-privacy-apply__block { margin: 0; }
  .hv-privacy-apply__block + .hv-privacy-apply__block { margin-top: 14px; }
  .hv-privacy-apply .hv-privacy-label { display: block; margin-bottom: 8px; font-size: 12px; }
  .hv-privacy-apply__title { margin: 0; font-size: 14px; font-weight: 700; color: var(--text-primary); }
  .hv-privacy-apply__sub { margin: 0 0 12px; font-size: 12.5px; line-height: 1.45; color: var(--text-muted); }
  .hv-privacy-apply .hv-privacy-choice-grid { gap: 8px; }
  .hv-privacy-choice--box,
  .hv-privacy-apply .hv-privacy-choice {
    flex-direction: row;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 10px;
    background: var(--bg-surface);
  }
  .hv-privacy-choice--box input[type="radio"],
  .hv-privacy-opt--box input[type="checkbox"] {
    margin: 2px 0 0;
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    cursor: pointer;
    accent-color: var(--teal);
  }
  .hv-privacy-apply .hv-privacy-choice__title { font-size: 13px; }
  .hv-privacy-apply .hv-privacy-choice__desc { font-size: 11.5px; line-height: 1.35; }
  .hv-privacy-apply .hv-privacy-choice__tick { display: none; }
  .hv-privacy-apply .hv-privacy-choice.is-on { box-shadow: none; }
  .hv-privacy-opt-list {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  .hv-privacy-apply .hv-privacy-opt,
  .hv-privacy-opt--box {
    gap: 8px;
    padding: 8px 10px;
    border-radius: 10px;
    background: var(--bg-surface);
  }
  .hv-privacy-apply .hv-privacy-opt .hv-privacy-choice__title { font-size: 12.5px; }
  .hv-privacy-apply .hv-privacy-opt .hv-privacy-choice__desc { font-size: 11px; line-height: 1.35; }
  .hv-privacy-error { margin: 6px 0 0; font-size: 12px; color: var(--text-danger); }

  @media (max-width: 960px) {
    .hv-privacy-workspace,
    .hv-privacy-two,
    .hv-privacy-steps,
    .hv-privacy-choice-grid,
    .hv-privacy-opt-list,
    .hv-privacy-rights,
    .hv-privacy-grid-2 { grid-template-columns: 1fr; }
    .hv-privacy-list { max-height: none; }
  }
  @media (max-width: 768px) {
    .hv-privacy-studio-shell {
      grid-template-columns: 1fr;
      min-height: calc(100vh - 96px);
    }
    .hv-privacy-studio-shell > .hv-privacy-rail { display: none; }
    .hv-privacy-toolbar { align-items: flex-start; }
    .hv-privacy-toolbar .hv-privacy-actions { width: 100%; }
  }
  @media (max-width: 640px) {
    .hv-privacy-metrics { grid-template-columns: 1fr; }
    .hv-privacy-accept-bar__inner { flex-direction: column; align-items: stretch; }
    .hv-privacy-accept-bar__inner button { width: 100%; justify-content: center; }
    .hv-privacy-hero { padding: 22px 18px; }
    .hv-privacy-hero__title { font-size: 22px; }
    .hv-privacy-lookup-search { flex-direction: column; }
    .hv-privacy.hv-privacy-studio { padding: 12px 16px 20px; }
    .hv-privacy.hv-privacy-board { padding: 12px 16px 24px; }
    .hv-privacy-board__title { font-size: 20px; }
    .hv-privacy-toolbar { padding: 14px 16px; }
    .hv-privacy-canvas__body,
    .hv-privacy-canvas__alerts { padding-left: 16px; padding-right: 16px; }
    .hv-privacy-banner { margin-left: 16px; margin-right: 16px; }
    .hv-privacy-gate { padding: 16px 12px 24px; }
    .hv-privacy-gate-modal { max-height: min(88vh, 760px); }
    .hv-privacy-control,
    button.hv-privacy-select {
      min-height: 40px;
      padding: 8px 10px;
      font-size: 13px;
    }
  }

  .ee-privacy-block {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .ee-privacy-note {
    border-radius: 8px;
    padding: 16px;
    border: 1px solid var(--ee-border);
    background: var(--ee-bg-muted);
  }

  .ee-privacy-whatsapp-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;
    cursor: pointer;
    font-size: 14px;
    line-height: 1.4;
  }

  .ee-privacy-whatsapp-check {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    accent-color: var(--ee-green);
    cursor: pointer;
  }

  .ee-privacy-wa-icon-wrap {
    display: flex;
    flex-shrink: 0;
    color: #25d366;
  }

  .ee-privacy-whatsapp-label {
    font-weight: 600;
    color: var(--ee-text);
  }

  .ee-privacy-plain {
    margin: 0;
    font-size: 13px;
    line-height: 1.55;
    font-weight: 400;
    color: var(--ee-text-muted);
  }

  .ee-flex-center {
    display: flex;
    justify-content: center;
  }

  .ee-presenter-head {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;
  }

  .ee-presenter-avatar {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--ee-gold);
    border: 2px solid rgba(251, 191, 36, 0.32);
    background: rgba(251, 191, 36, 0.1);
  }

  .ee-presenter-name {
    font-size: 16px;
    font-weight: 700;
    color: var(--ee-text);
  }

  .ee-presenter-role {
    font-size: 13px;
    color: var(--ee-text-muted);
  }

  .ee-stars-row {
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 2px;
  }

  .ee-presenter-note {
    margin-left: 6px;
    color: var(--ee-text-faint);
    font-size: 11px;
  }

  .ee-point-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .ee-point-text {
    font-size: 14px;
    color: var(--ee-text-sub);
  }

  .ee-kpi-row {
    display: flex;
    justify-content: center;
    gap: 48px;
  }

  .ee-kpi-num {
    font-size: 24px;
    font-weight: 900;
  }

  .ee-kpi-label {
    margin-top: 2px;
    font-size: 11px;
    color: var(--ee-text-faint);
  }

  .ee-mb-8 {
    margin-bottom: 8px;
  }

  .ee-mb-12 {
    margin-bottom: 12px;
  }

  .ee-mb-14 {
    margin-bottom: 14px;
  }

  .ee-mb-16 {
    margin-bottom: 16px;
  }

  .ee-mb-24 {
    margin-bottom: 24px;
  }

  .ee-mb-28 {
    margin-bottom: 28px;
  }

  .ee-mb-32 {
    margin-bottom: 32px;
  }

  .ee-mb-36 {
    margin-bottom: 36px;
  }

  .ee-mb-40 {
    margin-bottom: 40px;
  }

  .ee-mt-16 {
    margin-top: 16px;
  }

  .ee-mt-24 {
    margin-top: 24px;
  }

  .ee-mt-36 {
    margin-top: 36px;
  }

  @media (min-width: 901px) {
    .ee-screen {
      padding: 32px 24px;
    }

    .ee-mb-24 {
      margin-bottom: 18px;
    }

    .ee-mb-28 {
      margin-bottom: 22px;
    }

    .ee-mb-32 {
      margin-bottom: 24px;
    }

    .ee-mb-36 {
      margin-bottom: 28px;
    }

    .ee-mb-40 {
      margin-bottom: 32px;
    }

    .ee-mt-24 {
      margin-top: 18px;
    }

    .ee-mt-36 {
      margin-top: 28px;
    }
  }

  @media (max-width: 900px) {
    .ee-screen {
      padding: 48px 16px;
    }

    .ee-logo-float {
      top: 12px;
      left: auto;
      right: 16px;
      font-size: 16px;
    }

    .ee-back-btn {
      position: fixed;
      top: 14px;
      left: 16px;
      z-index: 40;
      margin: 0;
    }

    .ee-founders-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
      overflow-x: visible;
      padding-bottom: 0;
    }

    .ee-founder-card {
      min-height: 0;
      padding: 10px;
    }

    .ee-founder-avatar {
      width: 48px;
      height: 48px;
      border-radius: 10px;
      margin-bottom: 8px;
    }

    .ee-founder-name {
      font-size: 14px;
    }

    .ee-founder-inst,
    .ee-founder-companies {
      font-size: 10px;
    }

    .ee-founder-tag {
      font-size: 10px;
      padding: 2px 8px;
    }

    .ee-founder-companies {
      -webkit-line-clamp: 3;
    }

    .ee-stat-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 12px;
    }

    .ee-kpi-row {
      gap: 20px;
    }

    .ee-feature-stack {
      flex-direction: column;
      gap: 12px;
    }

    .ee-trait-wrap {
      flex-direction: column;
      gap: 12px;
    }
  }
`;

export function GlobalStyles() {
  return <style>{css}</style>;
}
